import React, { useState, useEffect, useRef, useMemo } from "react";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import { xml2js } from "../../json/xml2js";
import queryString from "query-string";
import { confirmAlert } from "react-confirm-alert";
import Modal from "react-bootstrap/Modal";
import "react-confirm-alert/src/react-confirm-alert.css";
import moment from "moment";
import ClientFormHeader from "../common/ClientFormHeader";
import FormButtonBar from "../common/FormButtonBar";
import Signature from "../common/Signature";
import Comment from "../common/Comment";
import BackIcon from "../common/BackIcon";
import ExpandDiv from "../common/ExpandDiv";
import ShareModal from "../common/ShareModal";
import {
  Select,
  Checkbox,
  FieldTable,
  Input,
  RadioButton,
  TextArea,
  ToggleSwitch,
} from "../common/ControlBundle";
import {
  getCareTeams,
  getConfig,
  getSubmission,
  saveForm,
  cloneForm,
  deleteFormSubmission,
  validateForm,
  handleFormChange,
  compareForms,
  getFormsySubmissionsForClient,
  sendShareEmail,
  getClientData,
  handleAddFieldTableRow,
  handleEditFieldTableRow,
  handleSaveFieldTableRow,
  handleDeleteFieldTableRow,
  handleFieldTableChange,
} from "../../scripts/formScripts";
import {
  getFieldName,
  getFieldValue,
  getFieldDate,
  ArrayCheck,
} from "../../scripts/helperScripts";
import { getSchema } from "./joi/validation";

function INKSPPSD(props) {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const selectedProgramId = useMemo(() => {
    return data.SelectedProgram?.["@value"];
  }, [data.SelectedProgram]);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram["@value"]
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, selectedProgramId, data, formsyDetails]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      Object.keys(formsyConfig).length > 0 ||
      loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadingConfig(true);
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadingConfig(false);

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [
    loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  const handleRowChange = (e, index, fieldname) => {
    const rowObj = handleFieldTableChange(data, e, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleSaveRow = (index, fieldname) => {
    const rowObj = handleSaveFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleDeleteRow = (index, fieldname) => {
    const rowObj = handleDeleteFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleEditRow = (index, fieldname) => {
    const rowObj = handleEditFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleAddRow = (fieldname, newRow) => {
    const rowObj = handleAddFieldTableRow(data, fieldname, newRow);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
    setCommentToEdit(undefined);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setExpandStatus(true);
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = JSON.parse(
      formsyConfig.HelperConfiguration[0]
    ).finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const [expandStatus, setExpandStatus] = useState(false);
  const [showNarrative, setShowNarrative] = useState("Hide");
  const [editSymptomIndex, setEditSymptomIndex] = useState();
  const [selectedSymptom, setSelectedSymptom] = useState("");
  const [checklistData, setChecklistData] = useState({});
  const [isChecklistModalOpen, setIsChecklistModalOpen] = useState(false);
  const [editMentalStatusIndex, setEditMentalStatusIndex] = useState();
  const [selectedCategory, setSelectedCategory] = useState("");
  const [mentalStatusData, setMentalStatusData] = useState({});
  const [isMentalStatusModalOpen, setIsMentalStatusModalOpen] = useState(false);

  const handleExpandStatusChange = (overrideValue, callback) => {
    if (overrideValue !== undefined) {
      setExpandStatus(overrideValue);
      if (callback) {
        window.setTimeout(callback, 5);
      }
    } else setExpandStatus(!expandStatus);
  };

  const divisionOptions = [
    { _id: "Fort Wayne", name: "Fort Wayne" },
    { _id: "Indianapolis", name: "Indianapolis" },
    { _id: "Merrillville", name: "Merrillville" },
    { _id: "Mishawaka", name: "Mishawaka" },
    { _id: "Southern Indiana", name: "Southern Indiana" },
  ];

  const countyOptions = [
    { _id: "Allen", name: "Allen" },
    { _id: "Benton", name: "Benton" },
    { _id: "Clark", name: "Clark" },
    { _id: "Dearborn", name: "Dearborn" },
    { _id: "Decatur", name: "Decatur" },
    { _id: "DeKalb", name: "DeKalb" },
    { _id: "Elkhart", name: "Elkhart" },
    { _id: "Floyd", name: "Floyd" },
    { _id: "Harrison", name: "Harrison" },
    { _id: "Jasper", name: "Jasper" },
    { _id: "Jefferson", name: "Jefferson" },
    { _id: "Kosciusko", name: "Kosciusko" },
    { _id: "LaGrange", name: "LaGrange" },
    { _id: "Lake", name: "Lake" },
    { _id: "LaPorte", name: "LaPorte" },
    { _id: "Marion", name: "Marion" },
    { _id: "Marshall", name: "Marshall" },
    { _id: "Newton", name: "Newton" },
    { _id: "Noble", name: "Noble" },
    { _id: "Ohio", name: "Ohio" },
    { _id: "Porter", name: "Porter" },
    { _id: "Pulaski", name: "Pulaski" },
    { _id: "Ripley", name: "Ripley" },
    { _id: "Scott", name: "Scott" },
    { _id: "St. Joseph", name: "St. Joseph" },
    { _id: "Starke", name: "Starke" },
    { _id: "Steuben", name: "Steuben" },
    { _id: "Switzerland", name: "Switzerland" },
    { _id: "Washington", name: "Washington" },
    { _id: "Whitley", name: "Whitley" },
    { _id: "Other", name: "Other" },
  ];

  const psdTypeOptions = [
    { _id: "Initial", name: "Initial" },
    { _id: "Review/Update", name: "Review/Update" },
  ];

  const yesNoOptions = [
    { _id: "Yes", name: "Yes" },
    { _id: "No", name: "No" },
  ];

  const yesNoUnknownOptions = [
    { _id: "Yes", name: "Yes" },
    { _id: "No", name: "No" },
    { _id: "Unknown", name: "Unknown" },
  ];

  const familyMemberOptions = [
    { _id: "Maternal Grandmother", name: "Maternal Grandmother" },
    { _id: "Maternal Grandfather", name: "Maternal Grandfather" },
    { _id: "Paternal Grandmother", name: "Paternal Grandmother" },
    { _id: "Paternal Grandfather", name: "Paternal Grandfather" },
    { _id: "Maternal Aunt", name: "Maternal Aunt" },
    { _id: "Maternal Uncle", name: "Maternal Uncle" },
    { _id: "Paternal Aunt", name: "Paternal Aunt" },
    { _id: "Paternal Uncle", name: "Paternal Uncle" },
    { _id: "Sibling", name: "Sibling" },
    { _id: "Cousin", name: "Cousin" },
    { _id: "Other", name: "Other" },
  ];

  const sexualOrientationOptions = [
    {
      _id: "Heterosexual",
      name: "Heterosexual",
    },
    {
      _id: "Gay",
      name: "Gay",
    },
    { _id: "Lesbian", name: "Lesbian" },
    { _id: "Bisexual", name: "Bisexual" },
    { _id: "Pansexual", name: "Pansexual" },
    { _id: "Polysexual", name: "Polysexual" },
    { _id: "Asexual", name: "Asexual" },
    { _id: "Queer / Questioning", name: "Queer / Questioning" },
    { _id: "Orientation not listed", name: "Orientation not listed" },
    { _id: "Prefer not to say", name: "Prefer not to say" },
    { _id: "N/A - is 5 or under", name: "N/A - is 5 or under" },
  ];

  const sexualHistoryOptions = [
    {
      _id: "Unknown",
      name: "Unknown",
    },
    {
      _id: "None Reported",
      name: "None Reported",
    },
    { _id: "Sexually Active", name: "Sexually Active" },
    { _id: "Victim of Sexual Abuse", name: "Victim of Sexual Abuse" },
    {
      _id: "Age Innappropriate Sexual Behavior",
      name: "Age Innappropriate Sexual Behavior",
    },
    {
      _id: "Sexually Active Out / Reactive Behavior",
      name: "Sexually Active Out / Reactive Behavior",
    },
    {
      _id: "Sexually Abusive / Offending Behavior",
      name: "Sexually Abusive / Offending Behavior",
    },
  ];

  const traumaTypeOptions = [
    { _id: "Physical Abuse", name: "Physical Abuse" },
    { _id: "Sexual Abuse", name: "Sexual Abuse" },
    { _id: "Neglect", name: "Neglect" },
    { _id: "Emotional Abuse", name: "Emotional Abuse" },
    { _id: "Domestic Violence", name: "Domestic Violence" },
    { _id: "Accident", name: "Accident" },
    { _id: "Acts of Violence", name: "Acts of Violence" },
    { _id: "Bullying", name: "Bullying" },
    { _id: "Death/Tragedy", name: "Death/Tragedy" },
    { _id: "Natural Disaster", name: "Natural Disaster" },
    { _id: "Community Violence", name: "Community Violence" },
    { _id: "Medical/Serious Illness", name: "Medical/Serious Illness" },
    { _id: "Abandonment", name: "Abandonment" },
    { _id: "War/Terrorism", name: "War/Terrorism" },
    { _id: "Exploitation/Trafficking", name: "Exploitation/Trafficking" },
    { _id: "Criminal Activity", name: "Criminal Activity" },
    { _id: "Caregiver Disruptions", name: "Caregiver Disruptions" },
    { _id: "Kidnapping", name: "Kidnapping" },
    { _id: "Other", name: "Other" },
  ];

  const symptomOptions = [
    {
      _id: "Symptoms of Trauma and Other Stressor-Related Disorders",
      name: "Symptoms of Trauma and Other Stressor-Related Disorders",
    },
    { _id: "Symptoms of Anxiety", name: "Symptoms of Anxiety" },
    { _id: "Symptoms of Depression", name: "Symptoms of Depression" },
    { _id: "Symptoms of ADHD", name: "Symptoms of ADHD" },
    { _id: "Symptoms of SUD", name: "Symptoms of SUD" },
    {
      _id: "Symptoms of Disruptive Mood Dysregulation Disorder",
      name: "Symptoms of Disruptive Mood Dysregulation Disorder",
    },
    {
      _id: "Symptoms of Conduct Disorder",
      name: "Symptoms of Conduct Disorder",
    },
    {
      _id: "Symptoms of Oppositional Defiance Disorder",
      name: "Symptoms of Oppositional Defiance Disorder",
    },
    { _id: "Symptoms of OCD", name: "Symptoms of OCD" },
    { _id: "Symptoms of Panic Disorder", name: "Symptoms of Panic Disorder" },
    { _id: "Symptoms of Bipolar", name: "Symptoms of Bipolar" },
    {
      _id: "Symptoms of a Personality Disorder",
      name: "Symptoms of a Personality Disorder",
    },
    { _id: "Other Symptoms", name: "Other Symptoms" },
  ];

  const legalHistoryOptions = [
    {
      _id: "Yes",
      name: "Yes — Family has a legal history (criminal, custody, divorce, etc.)",
    },
    {
      _id: "No",
      name: "No — Family does not have a legal history",
    },
  ];

  const cpsInvolvementOptions = [
    {
      _id: "Yes",
      name: "Yes — Family has past history of CPS involvement",
    },
    {
      _id: "No",
      name: "No — family does not have a past history of CPS involvement",
    },
  ];

  const completedOptions = [
    { _id: "Completed", name: "Completed" },
    { _id: "Not Completed", name: "Not Completed" },
  ];

  const diagnosisOptions = [
    {
      _id: "V60.9 (Z59.9) Unspecified Housing or Economic Problem",
      name: "V60.9 (Z59.9) Unspecified Housing or Economic Problem",
    },
    {
      _id: "V61.29 (Z62.898) Child Affected by Parental Relationship Distress",
      name: "V61.29 (Z62.898) Child Affected by Parental Relationship Distress",
    },
    {
      _id: "V62.29 (Z56.9) Other Problem Related to Employment",
      name: "V62.29 (Z56.9) Other Problem Related to Employment",
    },
    {
      _id: "V62.3 (Z55.9) Academic or Educational Problem",
      name: "V62.3 (Z55.9) Academic or Educational Problem",
    },
    {
      _id: "V62.5 (Z65.3) Problems Related to Other Legal Circumstance",
      name: "V62.5 (Z65.3) Problems Related to Other Legal Circumstance",
    },
    {
      _id: "V62.82 (Z63.4) Uncomplicated Bereavement",
      name: "V62.82 (Z63.4) Uncomplicated Bereavement",
    },
    {
      _id: "V62.89 (Z65.8) Other Problem Related to Psychosocial Circumstances",
      name: "V62.89 (Z65.8) Other Problem Related to Psychosocial Circumstances",
    },
    {
      _id: "V62.89 (R41.83) Borderline Intellectual Functioning",
      name: "V62.89 (R41.83) Borderline Intellectual Functioning",
    },
    {
      _id: "V62.9 (Z65.9) Unspecified Problem Related to Unspecified Psychosocial Circumstances",
      name: "V62.9 (Z65.9) Unspecified Problem Related to Unspecified Psychosocial Circumstances",
    },
    {
      _id: "V62.9 (Z60.9) Unspecified Problem Related to Social Environment",
      name: "V62.9 (Z60.9) Unspecified Problem Related to Social Environment",
    },
    {
      _id: "V71.09 (Z03.89)	No Diagnosis or Condition",
      name: "V71.09 (Z03.89)	No Diagnosis or Condition",
    },
    {
      _id: "Z55.9 Problem related to education and literacy, unspecified",
      name: "Z55.9 Problem related to education and literacy, unspecified",
    },
    {
      _id: "Z56.7 Other and unspecified problems related to employment",
      name: "Z56.7 Other and unspecified problems related to employment",
    },
    { _id: "Z59.1 Inadequate housing", name: "Z59.1 Inadequate housing" },
    {
      _id: "Z59.9 Problem related to housing and economic circumstances, unspecified",
      name: "Z59.9 Problem related to housing and economic circumstances, unspecified",
    },
    {
      _id: "Z60.8 Other problems related to social environment",
      name: "Z60.8 Other problems related to social environment",
    },
    {
      _id: "Z60.9 Problem related to social environment, unspecified",
      name: "Z60.9 Problem related to social environment, unspecified",
    },
    {
      _id: "995.54 (T74.12XA) Child Physical Abuse, Confirmed",
      name: "995.54 (T74.12XA) Child Physical Abuse, Confirmed",
    },
    {
      _id: "995.54 (T76.12XA) Child Physical Abuse, Suspected",
      name: "995.54 (T76.12XA) Child Physical Abuse, Suspected",
    },
    {
      _id: "995.52 (T74.02XA) Child Neglect, Confirmed",
      name: "995.52 (T74.02XA) Child Neglect, Confirmed",
    },
    {
      _id: "995.52 (T76.02XA) Child Neglect, Suspected",
      name: "995.52 (T76.02XA) Child Neglect, Suspected",
    },
    {
      _id: "995.81 (T74.11XA) Spouse or Partner Violence, Physical, Confirmed",
      name: "995.81 (T74.11XA) Spouse or Partner Violence, Physical, Confirmed",
    },
    {
      _id: "995.81 (T76.11XA) Spouse or Partner Violence, Physical, Suspected",
      name: "995.81 (T76.11XA) Spouse or Partner Violence, Physical, Suspected",
    },
    { _id: "Other", name: "Other" },
  ];

  const serviceTypeOptions = [
    { _id: "Concrete Services", name: "Concrete Services" },
    { _id: "Individual Therapy", name: "Individual Therapy" },
    {
      _id: "Family Preservation Case Management",
      name: "Family Preservation Case Management",
    },
    { _id: "Family/Couples Therapy", name: "Family/Couples Therapy" },
  ];

  const typeOfTreatmentOptions = [
    { _id: "Outpatient", name: "Outpatient" },
    { _id: "Intensive Outpatient", name: "Intensive Outpatient" },
    { _id: "Hospital", name: "Hospital" },
    { _id: "Residential", name: "Residential" },
    { _id: "Detox", name: "Detox" },
    { _id: "Court-Ordered Treatment", name: "Court-Ordered Treatment" },
    { _id: "Other", name: "Other" },
  ];

  const getSelfHarmRisk = (index) => {
    const risks = [];
    const shData = JSON.parse(data.SelfHarmDetails["@value"])[index];

    if (shData.SelfAbusive === "true")
      risks.push("Self-injurious or self-abusive");
    if (shData.SuicidalThreats === "true")
      risks.push("Suicidal threats/gestures");
    if (shData.SuicideAttempt === "true") risks.push("Suicide attempt");

    if (risks.length > 0) return risks.join(", ");
    else return "None reported";
  };

  const getHarmOthersRisk = (index) => {
    const risks = [];
    const hoData = JSON.parse(data.HarmingOthersDetails["@value"])[index];

    if (hoData.Thoughts === "true")
      risks.push("Thoughts of intentionally harming/endangering others");
    if (hoData.Threats === "true")
      risks.push("Threats to intentionally harm/endanger others");
    if (hoData.HarmOthers === "true")
      risks.push("Acts of intentionally harming/endangering others");
    if (hoData.HarmAnimals === "true")
      risks.push("Acts of intentionally harming/endangering animals");

    if (risks.length > 0) return risks.join(", ");
    else return "None reported";
  };

  const getFamilyMemberConcerns = (index) => {
    const concerns = [];
    const fmData = JSON.parse(data.FamilyHistoryDetails["@value"])[index];

    if (fmData.DomesticViolence === "true") concerns.push("Domestic Violence");
    if (fmData.Abuse === "true") concerns.push("Abuse / Neglect");
    if (fmData.CriminalActivity === "true") concerns.push("Criminal Activity");
    if (fmData.SubstanceUse === "true") concerns.push("Substance Use / Abuse");
    if (fmData.Medical === "true")
      concerns.push("Psychiatric / Neurological / Medical Conditions, etc.");
    if (fmData.Other === "true") concerns.push("Other");

    if (concerns.length > 0) return concerns.join(", ");
    else return "None reported";
  };

  const customValidate = (errs) => {
    if (
      (!data.ISFamily["@value"] || data.ISFamily["@value"] === "false") &&
      (!data.ISRecords["@value"] || data.ISRecords["@value"] === "false") &&
      (!data.ISSchool["@value"] || data.ISSchool["@value"] === "false") &&
      (!data.ISServiceProvider["@value"] ||
        data.ISServiceProvider["@value"] === "false") &&
      (!data.ISCourt["@value"] || data.ISCourt["@value"] === "false") &&
      (!data.ISDCS["@value"] || data.ISDCS["@value"] === "false") &&
      (!data.ISOther["@value"] || data.ISOther["@value"] === "false")
    ) {
      if (errs)
        errs.InformationSources =
          "At least one Information Source is required.";
      else
        errs = {
          InformationSources: "At least one Information Source is required.",
        };
    }

    const mentalStatusDetails = JSON.parse(data.MentalStatusDetails["@value"]);

    const missedCategory = mentalStatusDetails?.filter(
      (item) =>
        item.MentalStatusResults &&
        Object.keys(item.MentalStatusResults).length !== 8
    );

    if (missedCategory?.length > 0) {
      if (errs)
        errs.MentalStatusDetails =
          "All Mental Status Categories must have a value.";
      else
        errs = {
          MentalStatusDetails:
            "All Mental Status Categories must have a value.",
        };
    }

    // const treatmentRecommendations = JSON.parse(
    //   data.TreatmentRecommendations["@value"]
    // );

    // if (treatmentRecommendations.length === 0) {
    //   if (errs)
    //     errs.TreatmentRecommendations =
    //       "Treatment recommendations are required.";
    //   else
    //     errs = {
    //       TreatmentRecommendations: "Treatment recommendations are required.",
    //     };
    // }

    // if (data.NoSelfHarm["@value"] !== "true") {
    //   const selfHarmDetails = JSON.parse(data.SelfHarmDetails["@value"]);

    //   if (selfHarmDetails.length === 0) {
    //     if (errs)
    //       errs.SelfHarmDetails =
    //         "If there is a history or current risk of self-harm, details are required.";
    //     else
    //       errs = {
    //         SelfHarmDetails:
    //           "If there is a history or current risk of self-harm, details are required.",
    //       };
    //   }
    // }

    // if (data.NoHarmingOthers["@value"] !== "true") {
    //   const harmingOthersDetails = JSON.parse(
    //     data.HarmingOthersDetails["@value"]
    //   );

    //   if (harmingOthersDetails.length === 0) {
    //     if (errs)
    //       errs.HarmingOthersDetails =
    //         "If there is a history or current risk of harming others, details are required.";
    //     else
    //       errs = {
    //         HarmingOthersDetails:
    //           "If there is a history or current risk of harming others, details are required.",
    //       };
    //   }
    // }

    // const educationDetails = JSON.parse(data.EducationDetails["@value"]);

    // if (educationDetails.length === 0) {
    //   if (errs) errs.EducationDetails = "Education details are required.";
    //   else
    //     errs = {
    //       EducationDetails: "Education details are required.",
    //     };
    // }

    setErrors(errs);
    return errs;
  };

  const sectionsToNavigate = [
    { _id: "ReferralInformation", name: "Referral Information" },
    { _id: "FamilyHistorySection", name: "Family Environment and History" },
    {
      _id: "Demographics",
      name: "Family Culture/Ethnicity/Religion/Spirituality",
    },
    { _id: "FamilyWellbeing", name: "Family Wellbeing" },
    {
      _id: "MentalHealthTreatmentHistory",
      name: "Mental Health Treatment History",
    },
    { _id: "Lethality", name: "Lethality" },
    { _id: "Education", name: "Education/Employment" },
    {
      _id: "SubstanceUseHistory",
      name: "Alcohol/Drug Use History",
    },
    { _id: "TraumaHistory", name: "Trauma History" },
    { _id: "SymptomChecklist", name: "Symptom Checklist" },
    {
      _id: "Activities",
      name: "Activities of Daily Living",
    },
    { _id: "LegalHistory", name: "Legal History" },
    { _id: "AssessmentSummary", name: "Assessment Summary" },
    { _id: "Diagnosis", name: "Diagnosis/Evaluation" },
    {
      _id: "TreatmentRecommendations",
      name: "Treatment Recommendations/Needs",
    },
  ];

  const handleCategorySelected = (item, index, category) => {
    const statusData = {};
    item.MentalStatusResults?.[category]?.split(", ").forEach((item) => {
      statusData[item] = true;
    });
    setMentalStatusData(statusData);
    setSelectedCategory(category);
    setEditMentalStatusIndex(index);
    setIsMentalStatusModalOpen(true);
  };

  const handleSelectMentalStatusDetails = () => {
    const obj = { ...data };

    const allStatusDetails = JSON.parse(obj.MentalStatusDetails["@value"]);
    const mentalStatusResults =
      allStatusDetails[editMentalStatusIndex].MentalStatusResults || {};

    mentalStatusResults[selectedCategory] = Object.keys(mentalStatusData)
      .map((key) => {
        if (mentalStatusData[key]) {
          return key;
        }

        return null;
      })
      .join(", ");

    allStatusDetails[editMentalStatusIndex].MentalStatusResults =
      mentalStatusResults;
    obj.MentalStatusDetails["@value"] = JSON.stringify(allStatusDetails);

    setData(obj);
    setIsMentalStatusModalOpen(false);
    setMentalStatusData({});
  };

  const mentalStatusCategories = [
    "Appearance",
    "Behavior",
    "Speech",
    "Mood",
    "Affect",
    "Thought Process / Perception",
    "Cognition / Intellectual Functioning",
    "Insight / Judgment",
  ];

  const mentalStatusMap = {
    "Appearance": [
      { name: "Appropriate", label: "Appropriate" },
      { name: "Neat", label: "Neat" },
      { name: "Disheveled", label: "Disheveled" },
      { name: "Bizarre", label: "Bizarre" },
      { name: "Other", label: "Other" },
    ],
    "Behavior": [
      { name: "Cooperative", label: "Cooperative" },
      { name: "Guarded", label: "Guarded" },
      { name: "Hyperactive", label: "Hyperactive" },
      { name: "Agitated", label: "Agitated" },
      { name: "Paranoid", label: "Paranoid" },
      { name: "Tics", label: "Tics" },
      { name: "Impulsive", label: "Impulsive" },
      { name: "Aggressive", label: "Aggressive" },
      { name: "Withdrawn", label: "Withdrawn" },
      { name: "Bizarre", label: "Bizarre" },
      { name: "Rigidity", label: "Rigidity" },
      { name: "Other", label: "Other" },
    ],
    "Speech": [
      { name: "Normal", label: "Normal" },
      { name: "Slurred", label: "Slurred" },
      { name: "Pressured", label: "Pressured" },
      { name: "Tangential", label: "Tangential" },
      { name: "Incoherent", label: "Incoherent" },
      { name: "Stutters", label: "Stutters" },
      { name: "Other", label: "Other" },
    ],
    "Mood": [
      { name: "Euthymic", label: "Euthymic" },
      { name: "Anxious", label: "Anxious" },
      { name: "Angry", label: "Angry" },
      { name: "Depressed", label: "Depressed" },
      { name: "Euphoric", label: "Euphoric" },
      { name: "Irritable", label: "Irritable" },
      { name: "Fearful", label: "Fearful" },
      { name: "Other", label: "Other" },
    ],
    "Affect": [
      { name: "Full Range", label: "Full Range" },
      { name: "Restricted", label: "Restricted" },
      { name: "Blurred", label: "Blurred" },
      { name: "Flat", label: "Flat" },
      { name: "Inappropriate", label: "Inappropriate" },
      { name: "Other", label: "Other" },
    ],
    "Thought Process / Perception": [
      { name: "Logical", label: "Logical" },
      { name: "Preoccupations", label: "Preoccupations" },
      { name: "Delusions", label: "Delusions" },
      { name: "Hallucinations", label: "Hallucinations" },
      { name: "Racing", label: "Racing" },
      { name: "Disorganized", label: "Disorganized" },
      { name: "Concrete", label: "Concrete" },
      { name: "Suicidal Ideation", label: "Suicidal Ideation" },
      { name: "Homicidal Ideation", label: "Homicidal Ideation" },
      { name: "Other", label: "Other" },
    ],
    "Cognition / Intellectual Functioning": [
      { name: "No Impairment", label: "No Impairment" },
      { name: "Short Attention Span", label: "Short Attention Span" },
      { name: "Difficulty Concentrating", label: "Difficulty Concentrating" },
      { name: "Distractibility", label: "Distractibility" },
      { name: "Memory Impairment", label: "Memory Impairment" },
      { name: "Low Intelligence", label: "Low Intelligence" },
      { name: "Other", label: "Other" },
    ],
    "Insight / Judgment": [
      { name: "Appropriate", label: "Appropriate" },
      { name: "Fair", label: "Fair" },
      { name: "Impaired", label: "Impaired" },
      { name: "Other", label: "Other" },
    ],
  };

  const handleSymptomSelected = (item, index, value) => {
    item.Symptoms !== undefined
      ? item.Symptoms.push(value)
      : (item.Symptoms = [value]);

    handleRowChange(
      {
        currentTarget: {
          name: "Symptoms",
          value: item.Symptoms,
          type: "array",
        },
      },
      index,
      "Symptoms"
    );

    if (symptomMap[value] !== undefined) {
      setSelectedSymptom(value);
      setEditSymptomIndex(index);
      setIsChecklistModalOpen(true);
    }
  };

  const handleSelectSymptomDetails = () => {
    const obj = { ...data };

    const allSymptoms = JSON.parse(obj.Symptoms["@value"]);
    let symptomsDescription = allSymptoms[editSymptomIndex].Description || "";

    symptomsDescription = `${
      symptomsDescription !== ""
        ? `${symptomsDescription}, ${Object.values(checklistData).join(", ")}`
        : Object.values(checklistData).join(", ")
    }`;

    allSymptoms[editSymptomIndex].Description = symptomsDescription;
    obj.Symptoms["@value"] = JSON.stringify(allSymptoms);

    setData(obj);
    setIsChecklistModalOpen(false);
    setChecklistData({});
  };

  const symptomMap = {
    "Symptoms of Trauma and Other Stressor-Related Disorders": [
      { name: "TraumaticExposure", label: "Exposure to a traumatic event" },
      { name: "AvoidantTrauma", label: "Persistent avoidant behavior" },
      { name: "HypervigilanceTrauma", label: "Hypervigilance" },
      { name: "IntrusiveDreams", label: "Intrusive dreams/memories" },
      { name: "NegativeThoughts", label: "Negative thoughts and mood" },
      { name: "CocentrationProblems", label: "Problems with concentration" },
      {
        name: "RecklessBehaviorTrauma",
        label: "Irritable or reckless behavior",
      },
      { name: "SleepDisturbancesTrauma", label: "Sleep disturbances" },
      { name: "OtherTrauma", label: "Other" },
    ],
    "Symptoms of Anxiety": [
      { name: "AnxietyDisorderExcessWorry", label: "Excess worry" },
      { name: "AnxietyDisorderEasilyFatigued", label: "Easily fatigued" },
      { name: "MuscleTension", label: "Muscle tension" },
      { name: "Overworried", label: "Difficulty controlling worry" },
      {
        name: "AnxietyDisorderDifficultyConcentrating",
        label: "Difficulty concentrating",
      },
      { name: "SleepDisturbancesAnxiety", label: "Sleep difficulties" },
      { name: "RestlessAnxiety", label: "Restless" },
      { name: "Irritability", label: "Irritability" },
      { name: "OtherAnxiety", label: "Other" },
    ],
    "Symptoms of Depression": [
      {
        name: "DepressedMood",
        label: "Depressed mood most of the day, nearly every day",
      },
      { name: "SleepChanges", label: "Insomnia or hypersomnia" },
      { name: "Guilt", label: "Feelings of worthlessness/guilt" },
      {
        name: "DiminishedInterest",
        label: "Diminished interest or pleasure in most daily activities",
      },
      { name: "PurposelessMovement", label: "Purposeless movement" },
      {
        name: "DifficultyConcentratingDepression",
        label: "Inability to think/concentrate",
      },
      {
        name: "WeightChanges",
        label: "Significant weight loss or weight gain",
      },
      { name: "FatigueDepression", label: "Fatigue" },
      { name: "ThoughtsOfDeath", label: "Recurrent thoughts of death" },
      { name: "OtherDepression", label: "Other" },
    ],
    "Symptoms of ADHD": [
      {
        name: "AttentionHyperactivityEasilyDistracted",
        label: "Easily distracted",
      },
      {
        name: "DifficultyFollowThrough",
        label: "Difficulty following-through",
      },
      { name: "Forgetful", label: "Forgetful" },
      { name: "DifficultyListening", label: "Difficulty listening" },
      {
        name: "DisorganizedAttention",
        label: "Disorganized in tasks and activities",
      },
      { name: "Fidgety", label: "Fidgety" },
      { name: "LackOfDetails", label: "Lack of attention to detail" },
      { name: "LosesThings", label: "Loses things" },
      { name: "InterruptsOthers", label: "Interrupts others" },
      { name: "UnableToSitStill", label: "Unable to sit still" },
      { name: "ExcessiveTalking", label: "Excessive talking" },
      { name: "DifficultyWaiting", label: "Difficulty waiting for their turn" },
      { name: "OtherAttention", label: "Other" },
    ],
    "Symptoms of SUD": [
      {
        name: "PatternOfSubstanceUse",
        label:
          "Takes substances in large amounts and/or over long periods of time",
      },
      {
        name: "DesiresCutDownUse",
        label: "Persistent desire to cut down on substance use",
      },
      {
        name: "TimeSpentObtaining",
        label: "Spends a significant amount of time obtaining substance(s)",
      },
      { name: "Cravings", label: "Cravings for substances" },
      {
        name: "FailureOfResponsibilities",
        label: "Failure to fulfill major roles at work, school, or home",
      },
      {
        name: "NegativeImpact",
        label: "Use consistently negatively impacts relationships",
      },
      {
        name: "AbandonsActivities",
        label: "Giving up/reducing previously enjoyed activities",
      },
      { name: "Tolerance", label: "Tolerance" },
      { name: "SUWithdrawal", label: "Withdrawal" },
      { name: "SUOther", label: "Other" },
    ],
    "Symptoms of Disruptive Mood Dysregulation Disorder": [
      {
        name: "Temper",
        label:
          "Severe and recurrent temper outbursts (verbally and/or behaviorally)",
      },
      {
        name: "OutsideOutbursts",
        label: "Outbursts are outside of developmental level",
      },
      {
        name: "FrequentOutbursts",
        label: "Outbursts occur three or more times per week",
      },
      { name: "DMDDIrritable", label: "Irritable/angry mood most of the day" },
      {
        name: "OutburstsAroundOthers",
        label: "Symptoms occur in home, school and/or with peers",
      },
      { name: "DMDDOther", label: "DMDDOther" },
    ],
    "Symptoms of Conduct Disorder": [
      { name: "Aggression", label: "Aggression to people and/or animals" },
      { name: "DestructionOfProperty", label: "Destruction of Property" },
      { name: "Theft", label: "Deceitfulness or theft" },
      { name: "SeriousViolations", label: "Serious violations of rules" },
      { name: "OtherConductDisorder", label: "Other" },
    ],
    "Symptoms of Oppositional Defiance Disorder": [
      { name: "IrritableODD", label: "Angry/irritable mood" },
      { name: "Argumentative", label: "Argumentative/defiant" },
      { name: "Vindictive", label: "Vindictive" },
      { name: "OtherODD", label: "Other" },
    ],
    "Symptoms of OCD": [
      {
        name: "OCDPresence",
        label: "Presence of obsessions, compulsions, or both",
      },
      {
        name: "Obsessions",
        label:
          "Obsessions (recurrent and persistent thoughts, urges, or images)",
      },
      {
        name: "TimeConsuming",
        label:
          "Obsessions or compulsions are time consuming and cause distress/impairment",
      },
      {
        name: "Compulsions",
        label:
          "Compulsions/repetitive behaviors (hand washing, ordering, checking,) or mental acts (praying, counting, repeating words silently)",
      },
      {
        name: "ReduceAnxiety",
        label:
          "Behaviors and mental acts done to prevent or reduce anxiety or distress",
      },
      {
        name: "SuppressUrges",
        label:
          "Attempts to ignore or suppress thoughts, urges or images with other thought or action (compulsion)",
      },
      { name: "OCDOther", label: "Other" },
    ],
    "Symptoms of Panic Disorder": [
      {
        name: "PoundingHeart",
        label: "Palpitations, pounding heart, accelerated heart rate",
      },
      { name: "PanicDisorderSweating", label: "Sweating" },
      { name: "Trembling", label: "Trembling/shaking" },
      { name: "ShortnessOfBreath", label: "Shortness of breath" },
      { name: "Choking", label: "Feelings of choking" },
      { name: "ChestPain", label: "Chest pain/discomfort" },
      { name: "PanicDisorderNausea", label: "Nausea/abdominal discomfort" },
      { name: "Dizzy", label: "Dizzy/faint" },
      { name: "Chills", label: "Chills" },
      { name: "Derealization", label: "Derealization or depersonalization" },
      { name: "FearOfCrazy", label: "Fear of losing control/going crazy" },
      { name: "FearOfDying", label: "Fear of dying" },
      { name: "PanicDisorderOther", label: "Other" },
    ],
    "Symptoms of a Bipolar": [
      { name: "ManicEpisodes", label: "Manic (or hypomanic) Episodes" },
      { name: "DepressiveEpisodes", label: "Depressive Episodes" },
      {
        name: "MoodChanges",
        label:
          "Significant changes in mood and activity level that impact functioning",
      },
      { name: "BipolarOther", label: "Other" },
    ],
    "Symptoms of a Personality Disorder": [
      {
        name: "Frantic",
        label: "Frantic efforts to avoid real or imagined abandonment",
      },
      {
        name: "Impulsive",
        label:
          "Impulsivity in at least 2: Spending, Sex, Substance Use, Reckless Driving, Binge Eating",
      },
      {
        name: "UnstableRelationships",
        label:
          "A pattern of unstable and intense interpersonal relationships (Alternating between extremes of idealization and devaluation)",
      },
      {
        name: "IdentityDisturbances",
        label:
          "Identity Disturbances: Markedly and persistently unstable self-image or sense of self",
      },
      {
        name: "RecurrentSelfHarm",
        label:
          "Recurrent suicidal behavior/gestures/threats or self-mutilating behavior",
      },
      {
        name: "ReactiveMood",
        label:
          "ffective instability due to marked reactivity of mood (e.g., intense dysphoria, irritability, or anxiety)",
      },
      { name: "Emptiness", label: "Chronic feelings of emptiness" },
      {
        name: "IntenseAnger",
        label: "Inappropriate, intense anger or difficulty controlling anger",
      },
      {
        name: "Paranoia",
        label:
          "Transient, stress-related paranoid ideation or severe dissociative symptoms",
      },
      { name: "Other", label: "Other" },
    ],
  };

  ////UI
  return (
    <>
      {isChecklistModalOpen ? (
        <Modal show={isChecklistModalOpen} animation={false}>
          <Modal.Header>
            <Modal.Title>Symptom Checklist</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {symptomMap[selectedSymptom].map((symptom) => {
              return (
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name={symptom.name}
                      label={symptom.label}
                      divClassName="ms-3"
                      checked={checklistData[symptom.name] !== undefined}
                      onChange={(e) => {
                        const obj = { ...checklistData };

                        if (e.currentTarget.checked)
                          obj[symptom.name] = symptom.label;
                        else delete obj[symptom.name];

                        setChecklistData(obj);
                      }}
                    />
                  </div>
                </div>
              );
            })}
            <div className="row">
              <div className="col">
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    handleSelectSymptomDetails();
                  }}
                >
                  Select
                </button>
                <button
                  className="btn btn-danger ms-2"
                  onClick={() => {
                    setIsChecklistModalOpen(false);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </Modal.Body>
        </Modal>
      ) : null}
      {isMentalStatusModalOpen ? (
        <Modal show={isMentalStatusModalOpen} animation={false}>
          <Modal.Header>
            <Modal.Title>{selectedCategory}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {mentalStatusMap[selectedCategory].map((statusOption) => {
              return (
                <div className="row" key={statusOption.name}>
                  <div className="col">
                    <Checkbox
                      name={statusOption.name}
                      label={statusOption.label}
                      divClassName="ms-3"
                      checked={
                        mentalStatusData[statusOption.name] !== undefined
                      }
                      onChange={(e) => {
                        const obj = { ...mentalStatusData };

                        if (e.currentTarget.checked)
                          obj[statusOption.name] = statusOption.label;
                        else delete obj[statusOption.name];

                        setMentalStatusData(obj);
                      }}
                    />
                  </div>
                </div>
              );
            })}
            <div className="row">
              <div className="col">
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    handleSelectMentalStatusDetails();
                  }}
                >
                  Select
                </button>
                <button
                  className="btn btn-danger ms-2"
                  onClick={() => {
                    setIsMentalStatusModalOpen(false);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </Modal.Body>
        </Modal>
      ) : null}
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        ></ClientFormHeader>
        <FormButtonBar
          config={
            formsyConfig.HelperConfiguration
              ? JSON.parse(formsyConfig.HelperConfiguration[0]).buttonBarConfig
              : {
                  sign: {
                    display: false,
                    initialSignatureType: "supervisor",
                  },
                  clone: {
                    display: false,
                  },
                  delete: {
                    display: false,
                  },
                  share: {
                    display: false,
                  },
                  comment: {
                    display: false,
                  },
                }
          }
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          customValidate={customValidate}
          sectionsToNavigate={sectionsToNavigate}
          beforePrintFunction={(callback) => {
            handleExpandStatusChange(true, callback);
          }}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <div className="row">
                <div className="col">
                  <Input
                    name="DateCompleted"
                    label="Date of Assessment:"
                    type="date"
                    error={errors.DateCompleted}
                    value={data.DateCompleted["@value"]}
                    onChange={handleChange}
                    identical={compare("DateCompleted")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <RadioButton
                    name="PSDType"
                    label="Type of Assessment:"
                    error={errors.PSDType}
                    value={data.PSDType["@value"]}
                    options={psdTypeOptions}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  {data.PSDType["@value"] === "Review/Update" && (
                    <Input
                      name="ReviewReason"
                      label="Reason:"
                      error={errors.ReviewReason}
                      value={getFieldValue(data.ReviewReason)}
                      onChange={handleChange}
                      identical={compare("ReviewReason")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  )}
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <Input
                    name="ReferralMembers"
                    label="Family Members on Referral:"
                    error={errors.ReferralMembers}
                    value={getFieldValue(data.ReferralMembers)}
                    onChange={handleChange}
                    identical={compare("ReferralMembers")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <Input
                    name="CaseMember"
                    label="Family Member listed under Case ID:"
                    error={errors.CaseMember}
                    value={getFieldValue(data.CaseMember)}
                    onChange={handleChange}
                    identical={compare("CaseMember")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <Input
                    name="MaGIKCaseID"
                    label="MaGIK Case ID:"
                    error={errors.MaGIKCaseID}
                    value={getFieldValue(data.MaGIKCaseID)}
                    onChange={handleChange}
                    identical={compare("MaGIKCaseID")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <Input
                    name="Caseworker"
                    label="Caseworker:"
                    error={errors.Caseworker}
                    value={getFieldValue(data.Caseworker)}
                    onChange={handleChange}
                    identical={compare("Caseworker")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <Select
                    name="Division"
                    label="SAFY Division:"
                    error={errors.Division}
                    value={getFieldValue(data.Division)}
                    options={divisionOptions}
                    onChange={handleChange}
                    identical={compare("Division")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <Select
                    name="County"
                    label="County:"
                    error={errors.County}
                    value={getFieldValue(data.County)}
                    options={countyOptions}
                    onChange={handleChange}
                    identical={compare("County")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  {getFieldValue(data.County) === "Other" && (
                    <Input
                      name="CountyDetails"
                      error={errors.CountyDetails}
                      value={getFieldValue(data.CountyDetails)}
                      onChange={handleChange}
                      identical={compare("CountyDetails")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  )}
                </div>
              </div>
              <h5>Information Sources:</h5>
              <div className="container">
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="ISFamily"
                      label="Family Members"
                      checked={data.ISFamily["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="ISRecords"
                      label="Records"
                      checked={data.ISRecords["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="ISSchool"
                      label="School"
                      checked={data.ISSchool["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="ISServiceProvider"
                      label="Other Service Provider"
                      checked={data.ISServiceProvider["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="ISCourt"
                      label="Court"
                      checked={data.ISCourt["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="ISDCS"
                      label="DCS"
                      checked={data.ISDCS["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="ISOther"
                      label="Other/Comments"
                      checked={data.ISOther["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.ISOther["@value"] === "true" && (
                      <Input
                        name="ISOtherDetails"
                        error={errors.ISOtherDetails}
                        value={data.ISOtherDetails["@value"]}
                        onChange={handleChange}
                        identical={compare("ISOtherDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                  </div>
                  <div className="col"></div>
                </div>
              </div>
              {errors.InformationSources !== undefined && (
                <div className="row">
                  <div className="col">
                    <div className="alert alert-danger">
                      {errors.InformationSources}
                    </div>
                  </div>
                </div>
              )}
              <div className="clearfix">
                <div className="float-start">
                  <button
                    className="btn btn-primary mb-2"
                    onClick={(e) => {
                      handleExpandStatusChange();
                    }}
                  >
                    {expandStatus === true ? "Collapse All" : "Expand All"}
                  </button>
                </div>
                <div className="float-end d-flex">
                  <div className="me-2">
                    Clinical Observations/Family Clinical Summary:
                  </div>
                  <ToggleSwitch
                    values={["Show", "Hide"]}
                    selected={showNarrative}
                    onChange={setShowNarrative}
                  />
                </div>
              </div>
              <ExpandDiv
                id="ReferralInformation"
                expandStatus={expandStatus}
                className="mt-2"
                title="Referral Information"
                tooltip="Precipitating factors that led to the need for this assessment. Please include a statement in the family's own words as well as sources for the assessment/family's response to referral"
              >
                <Input
                  name="ReferralReason"
                  label="Reason for Referral:"
                  error={errors.ReferralReason}
                  value={data.ReferralReason["@value"]}
                  onChange={handleChange}
                  identical={compare("ReferralReason")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <TextArea
                  name="PresentingProblem"
                  label="Family's Description of the Problem(s):"
                  error={errors.PresentingProblem}
                  value={data.PresentingProblem["@value"]}
                  onChange={handleChange}
                  identical={compare("PresentingProblem")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <TextArea
                  name="ProtectiveFactors"
                  label="Protective Factors, Family Strengths, and Interests:"
                  error={errors.ProtectiveFactors}
                  value={data.ProtectiveFactors["@value"]}
                  onChange={handleChange}
                  identical={compare("ProtectiveFactors")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              </ExpandDiv>
              <ExpandDiv
                id="FamilyHistorySection"
                expandStatus={expandStatus}
                className="mt-2"
                title="Family Environment and History"
              >
                <div title="Individuals currently in the home, ages, DOB, family role">
                  Current Household: <i className="fas fa-info-circle"></i>
                </div>
                <TextArea
                  name="CurrentHousehold"
                  error={errors.CurrentHousehold}
                  value={data.CurrentHousehold["@value"]}
                  onChange={handleChange}
                  identical={compare("CurrentHousehold")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <div title="Individuals who have a close relationship to client, not living in the home. Include their relationship to the family, their role in family's life, and how they serve as a support to the family.">
                  Other Family Supports: <i className="fas fa-info-circle"></i>
                </div>
                <TextArea
                  name="OtherFamilySupports"
                  error={errors.OtherFamilySupports}
                  value={data.OtherFamilySupports["@value"]}
                  onChange={handleChange}
                  identical={compare("OtherFamilySupports")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <div className="row pt-2">
                  <div className="col">
                    <RadioButton
                      name="CaregiverCooperation"
                      label="Cooperation between caregivers on discipline and care:"
                      error={errors.CaregiverCooperation}
                      value={data.CaregiverCooperation["@value"]}
                      options={yesNoOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <RadioButton
                      name="SupervisionDifficulties"
                      label="Difficulties with the supervision/monitoring of child:"
                      error={errors.SupervisionDifficulties}
                      value={data.SupervisionDifficulties["@value"]}
                      options={yesNoOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row pt-2">
                  <div className="col">
                    {data.CaregiverCooperation["@value"] === "No" && (
                      <Input
                        name="CaregiverCooperationDetails"
                        label="Explain:"
                        error={errors.CaregiverCooperationDetails}
                        value={data.CaregiverCooperationDetails["@value"]}
                        onChange={handleChange}
                        identical={compare("CaregiverCooperationDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                  </div>
                  <div className="col">
                    {data.SupervisionDifficulties["@value"] === "Yes" && (
                      <Input
                        name="SupervisionDifficultiesDetails"
                        label="Describe:"
                        error={errors.SupervisionDifficultiesDetails}
                        value={data.SupervisionDifficultiesDetails["@value"]}
                        onChange={handleChange}
                        identical={compare("SupervisionDifficultiesDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                  </div>
                </div>
                <TextArea
                  name="DisciplinePractices"
                  label="Discipline practices:"
                  error={errors.DisciplinePractices}
                  value={data.DisciplinePractices["@value"]}
                  onChange={handleChange}
                  identical={compare("DisciplinePractices")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <TextArea
                  name="Interaction"
                  label="Interaction between caregivers and child(ren):"
                  error={errors.Interaction}
                  value={data.Interaction["@value"]}
                  onChange={handleChange}
                  identical={compare("Interaction")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <h5>Extended Family History</h5>
                <Checkbox
                  name="FamilyHistoryUnknown"
                  label="Family History Unknown"
                  divClassName="ms-3"
                  checked={data.FamilyHistoryUnknown["@value"] === "true"}
                  commentsHandler={handleComments}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                {data.FamilyHistoryUnknown["@value"] !== "true" ? (
                  <FieldTable
                    name="FamilyHistoryDetails"
                    commentsHandler={handleComments}
                  >
                    <div className="container border-top border-start border-end mb-2">
                      <div className="row border-bottom">
                        <div className="col border-end py-2">Family Member</div>
                        <div className="col border-end py-2">Concern/Issue</div>
                        <div className="col py-2">Explanation/Comments</div>
                      </div>
                      {data.FamilyHistoryDetails["@value"] &&
                        JSON.parse(data.FamilyHistoryDetails["@value"]).map(
                          (item, index) => (
                            <div key={item._id} className="row border-bottom">
                              <div className="col border-end py-2 d-flex">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <>
                                    <i
                                      className="fas fa-save me-2 d-print-none"
                                      onClick={() => {
                                        handleSaveRow(
                                          index,
                                          "FamilyHistoryDetails"
                                        );
                                      }}
                                    ></i>
                                    <i
                                      className="far fa-trash-alt me-2 d-print-none"
                                      onClick={() => {
                                        handleDeleteRow(
                                          index,
                                          "FamilyHistoryDetails"
                                        );
                                      }}
                                    ></i>
                                    <Select
                                      name="FamilyMember"
                                      value={item.FamilyMember}
                                      options={familyMemberOptions}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "FamilyHistoryDetails"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                    {item.FamilyMember === "Other" ? (
                                      <Input
                                        name="OtherFamilyMember"
                                        value={item.OtherFamilyMember}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "FamilyHistoryDetails"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : null}
                                  </>
                                ) : (
                                  <>
                                    <i
                                      className="fas fa-pen me-2 d-print-none"
                                      onClick={() => {
                                        handleEditRow(
                                          index,
                                          "FamilyHistoryDetails"
                                        );
                                      }}
                                    ></i>
                                    {item.FamilyMember}
                                  </>
                                )}
                              </div>
                              <div className="col border-end py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <div className="container">
                                    <div className="row">
                                      <div className="col">
                                        <Checkbox
                                          name={`DomesticViolence-${index}`}
                                          label="Domestic Violence"
                                          checked={
                                            item.DomesticViolence === "true"
                                          }
                                          onChange={(e) => {
                                            e.currentTarget = {
                                              ...e.currentTarget,
                                              type: "checkbox",
                                              name: e.currentTarget.name.split(
                                                "-"
                                              )[0],
                                            };
                                            handleRowChange(
                                              e,
                                              index,
                                              "FamilyHistoryDetails"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </div>
                                      <div className="col">
                                        <Checkbox
                                          name={`Abuse-${index}`}
                                          label="Abuse / Neglect"
                                          checked={item.Abuse === "true"}
                                          onChange={(e) => {
                                            e.currentTarget = {
                                              ...e.currentTarget,
                                              type: "checkbox",
                                              name: e.currentTarget.name.split(
                                                "-"
                                              )[0],
                                            };
                                            handleRowChange(
                                              e,
                                              index,
                                              "FamilyHistoryDetails"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </div>
                                      <div className="col">
                                        <Checkbox
                                          name={`CriminalActivity-${index}`}
                                          label="Criminal Activity"
                                          checked={
                                            item.CriminalActivity === "true"
                                          }
                                          onChange={(e) => {
                                            e.currentTarget = {
                                              ...e.currentTarget,
                                              type: "checkbox",
                                              name: e.currentTarget.name.split(
                                                "-"
                                              )[0],
                                            };
                                            handleRowChange(
                                              e,
                                              index,
                                              "FamilyHistoryDetails"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </div>
                                    </div>
                                    <div className="row">
                                      <div className="col">
                                        <Checkbox
                                          name={`SubstanceUse-${index}`}
                                          label="Substance Use / Abuse"
                                          checked={item.SubstanceUse === "true"}
                                          onChange={(e) => {
                                            e.currentTarget = {
                                              ...e.currentTarget,
                                              type: "checkbox",
                                              name: e.currentTarget.name.split(
                                                "-"
                                              )[0],
                                            };
                                            handleRowChange(
                                              e,
                                              index,
                                              "FamilyHistoryDetails"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </div>
                                      <div className="col-8">
                                        <Checkbox
                                          name={`Medical-${index}`}
                                          label="Psychiatric / Neurological / Medical Conditions, etc."
                                          checked={item.Medical === "true"}
                                          onChange={(e) => {
                                            e.currentTarget = {
                                              ...e.currentTarget,
                                              type: "checkbox",
                                              name: e.currentTarget.name.split(
                                                "-"
                                              )[0],
                                            };
                                            handleRowChange(
                                              e,
                                              index,
                                              "FamilyHistoryDetails"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </div>
                                    </div>
                                    <div className="row">
                                      <div className="col">
                                        <Checkbox
                                          name={`Other-${index}`}
                                          label="Other"
                                          checked={item.Other === "true"}
                                          onChange={(e) => {
                                            e.currentTarget = {
                                              ...e.currentTarget,
                                              type: "checkbox",
                                              name: e.currentTarget.name.split(
                                                "-"
                                              )[0],
                                            };
                                            handleRowChange(
                                              e,
                                              index,
                                              "FamilyHistoryDetails"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  getFamilyMemberConcerns(index)
                                )}
                              </div>
                              <div className="col py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <Input
                                    name="Comments"
                                    value={item.Comments}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "FamilyHistoryDetails"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  item.Comments
                                )}
                              </div>
                            </div>
                          )
                        )}
                    </div>
                    {!formDisabled ? (
                      <button
                        className="btn btn-primary mb-2"
                        onClick={() => {
                          handleAddRow("FamilyHistoryDetails", {
                            IsEditable: true,
                          });
                        }}
                      >
                        Add Family Member
                      </button>
                    ) : null}
                    {errors.FamilyHistoryDetails && (
                      <div className="alert alert-danger">
                        {errors.FamilyHistoryDetails}
                      </div>
                    )}
                  </FieldTable>
                ) : null}
              </ExpandDiv>
              <ExpandDiv
                id="Demographics"
                expandStatus={expandStatus}
                className="mt-2"
                title="Family Culture/Ethnicity/Religion/Spirituality"
              >
                <div className="container">
                  <div className="row">
                    <div
                      className="col"
                      title="Culture broadly refers to all the ways an individual understands their identity in terms of race, ethnicity, gender, sexual orientation, social class, religion/spirituality and language"
                    >
                      Identified cultural group:{" "}
                      <i className="fas fa-info-circle"></i>
                    </div>
                    <div className="col">
                      <Input
                        name="CulturalGroup"
                        error={errors.CulturalGroup}
                        value={data.CulturalGroup["@value"]}
                        onChange={handleChange}
                        identical={compare("CulturalGroup")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      Special cultural traditions/celebrations/rituals:
                    </div>
                    <div className="col">
                      <Input
                        name="TraditionsComments"
                        error={errors.TraditionsComments}
                        value={data.TraditionsComments["@value"]}
                        onChange={handleChange}
                        identical={compare("TraditionsComments")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">Languages spoken:</div>
                    <div className="col">
                      <Input
                        name="LanguagesSpoken"
                        error={errors.LanguagesSpoken}
                        value={data.LanguagesSpoken["@value"]}
                        onChange={handleChange}
                        identical={compare("LanguagesSpoken")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      Identified religion/belief system:
                    </div>
                    <div className="col">
                      <Input
                        name="Religion"
                        error={errors.Religion}
                        value={data.Religion["@value"]}
                        onChange={handleChange}
                        identical={compare("Religion")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">Role of faith in client's life:</div>
                    <div className="col">
                      <Input
                        name="ReligionRole"
                        error={errors.ReligionRole}
                        value={data.ReligionRole["@value"]}
                        onChange={handleChange}
                        identical={compare("ReligionRole")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      Gender identity &amp; sexual orientation:
                    </div>
                    <div className="col">
                      <Input
                        name="GenderOrientation"
                        error={errors.GenderOrientation}
                        value={data.GenderOrientation["@value"]}
                        onChange={handleChange}
                        identical={compare("GenderOrientation")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                </div>
              </ExpandDiv>
              <ExpandDiv
                id="FamilyWellbeing"
                expandStatus={expandStatus}
                className="mt-2"
                title="Family Wellbeing"
                tooltip="Please make sure you list which family member(s) has(have) the identified medical issues."
              >
                <Input
                  name="PCP"
                  label="Primary Care Physician:"
                  error={errors.PCP}
                  value={data.PCP["@value"]}
                  onChange={handleChange}
                  identical={compare("PCP")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <Input
                  name="LastMedicalExamDates"
                  label="Date(s) of last medical exam for child(ren):"
                  error={errors.LastMedicalExamDates}
                  value={data.LastMedicalExamDates["@value"]}
                  onChange={handleChange}
                  identical={compare("LastMedicalExamDates")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <div className="container">
                  <div className="row">
                    <div className="col">Issue(s)</div>
                    <div className="col">Family Member(s) and Description</div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Checkbox
                        name="Allergies"
                        label="Allergies"
                        divClassName="ms-3"
                        checked={data.Allergies["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.Allergies["@value"] === "true" && (
                        <Input
                          name="AllergiesDetails"
                          error={errors.AllergiesDetails}
                          value={data.AllergiesDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("AllergiesDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Checkbox
                        name="MedicalConditions"
                        label="Medical conditions/concerns"
                        divClassName="ms-3"
                        checked={data.MedicalConditions["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.MedicalConditions["@value"] === "true" && (
                        <Input
                          name="MedicalConditionsDetails"
                          error={errors.MedicalConditionsDetails}
                          value={data.MedicalConditionsDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("MedicalConditionsDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Checkbox
                        name="NutritionalConcerns"
                        label="Nutritional concerns"
                        divClassName="ms-3"
                        checked={data.NutritionalConcerns["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.NutritionalConcerns["@value"] === "true" && (
                        <Input
                          name="NutritionalConcernsDetails"
                          error={errors.NutritionalConcernsDetails}
                          value={data.NutritionalConcernsDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("NutritionalConcernsDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Checkbox
                        name="DentalConcerns"
                        label="Dental concerns"
                        divClassName="ms-3"
                        checked={data.DentalConcerns["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.DentalConcerns["@value"] === "true" && (
                        <Input
                          name="DentalConcernsDetails"
                          error={errors.DentalConcernsDetails}
                          value={data.DentalConcernsDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("DentalConcernsDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Checkbox
                        name="SleepConcerns"
                        label="Sleep concerns"
                        divClassName="ms-3"
                        checked={data.SleepConcerns["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.SleepConcerns["@value"] === "true" && (
                        <Input
                          name="SleepConcernsDetails"
                          error={errors.SleepConcernsDetails}
                          value={data.SleepConcernsDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("SleepConcernsDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Checkbox
                        name="Hospitalizations"
                        label="Hospitalizations/Surgery"
                        divClassName="ms-3"
                        checked={data.Hospitalizations["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.Hospitalizations["@value"] === "true" && (
                        <Input
                          name="HospitalizationsDetails"
                          error={errors.HospitalizationsDetails}
                          value={data.HospitalizationsDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("HospitalizationsDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Checkbox
                        name="HeadInjury"
                        label="Head Injury"
                        divClassName="ms-3"
                        checked={data.HeadInjury["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.HeadInjury["@value"] === "true" && (
                        <Input
                          name="HeadInjuryDetails"
                          error={errors.HeadInjuryDetails}
                          value={data.HeadInjuryDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("HeadInjuryDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                </div>
                <RadioButton
                  name="Undiagnosed"
                  label="Symptoms that suggest an undiagnosed medical illness that may be causing or contributing to mental health symptoms:"
                  error={errors.Undiagnosed}
                  value={data.Undiagnosed["@value"]}
                  options={yesNoOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                  className="mt-2"
                />
                {data.Undiagnosed["@value"] === "Yes" && (
                  <TextArea
                    name="UndiagnosedComments"
                    label="Comments:"
                    error={errors.UndiagnosedComments}
                    value={data.UndiagnosedComments["@value"]}
                    onChange={handleChange}
                    identical={compare("UndiagnosedComments")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    placeholder="Only list physical symptoms that are associated with a medical illness, not a mental health related issue"
                  />
                )}
                <div title="Prenatal care, exposure to tobacco/alcohol/drugs/violence, birth complications, etc.">
                  Pregnancy History: <i className="fas fa-info-circle"></i>
                </div>
                <TextArea
                  name="PregnancyHistory"
                  error={errors.PregnancyHistory}
                  value={data.PregnancyHistory["@value"]}
                  onChange={handleChange}
                  identical={compare("PregnancyHistory")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <h5>Developmental History of Children:</h5>
                <div className="container">
                  <div className="row">
                    <div className="col">
                      Developmental milestones met on time:
                    </div>
                    <div className="col">
                      <RadioButton
                        name="MilestonesMet"
                        error={errors.MilestonesMet}
                        value={data.MilestonesMet["@value"]}
                        options={yesNoUnknownOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      {data.MilestonesMet["@value"] === "No" ? (
                        <Input
                          name="MilestonesMetDetails"
                          label="Describe:"
                          error={errors.MilestonesMetDetails}
                          value={data.MilestonesMetDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("MilestonesMetDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      Developmental delays or special needs:
                    </div>
                    <div className="col">
                      <RadioButton
                        name="SpecialNeeds"
                        error={errors.SpecialNeeds}
                        value={data.SpecialNeeds["@value"]}
                        options={yesNoUnknownOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      {data.SpecialNeeds["@value"] === "Yes" && (
                        <TextArea
                          name="SpecialNeedsDetails"
                          label="Describe:"
                          error={errors.SpecialNeedsDetails}
                          value={data.SpecialNeedsDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("SpecialNeedsDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      Specialized Services (Speech/Occupational/Physical
                      Therapy/Assistive Devices):
                    </div>
                    <div className="col">
                      <RadioButton
                        name="SpecializedServices"
                        error={errors.SpecializedServices}
                        value={data.SpecializedServices["@value"]}
                        options={yesNoUnknownOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      {data.SpecializedServices["@value"] === "Yes" && (
                        <Input
                          name="SpecializedServicesDetails"
                          label="Describe:"
                          error={errors.SpecializedServicesDetails}
                          value={data.SpecializedServicesDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("SpecializedServicesDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                </div>
                <h5>Medications</h5>
                <RadioButton
                  name="HasMedications"
                  label="Are any family members currently prescribed medication?"
                  error={errors.HasMedications}
                  value={data.HasMedications["@value"]}
                  options={yesNoOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.HasMedications["@value"] !== "No" ? (
                  <FieldTable
                    name="CurrentMedicationDetails"
                    commentsHandler={handleComments}
                  >
                    <div className="container border-top border-start border-end mb-2">
                      <div className="row border-bottom">
                        <div className="col border-end py-2">Family Member</div>
                        <div className="col border-end py-2">
                          Type of Medication
                        </div>
                        <div className="col border-end py-2">Dosage</div>
                        <div className="col py-2">Purpose</div>
                      </div>
                      {data.CurrentMedicationDetails["@value"]
                        ? JSON.parse(
                            data.CurrentMedicationDetails["@value"]
                          ).map((item, index) => (
                            <div key={item._id} className="row border-bottom">
                              <div className="col border-end py-2 d-flex">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <>
                                    <i
                                      className="fas fa-save me-2 d-print-none"
                                      onClick={() => {
                                        handleSaveRow(
                                          index,
                                          "CurrentMedicationDetails"
                                        );
                                      }}
                                    ></i>
                                    <i
                                      className="far fa-trash-alt me-2 d-print-none"
                                      onClick={() => {
                                        handleDeleteRow(
                                          index,
                                          "CurrentMedicationDetails"
                                        );
                                      }}
                                    ></i>
                                    <Input
                                      name="FamilyMember"
                                      value={item.FamilyMember}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "CurrentMedicationDetails"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  </>
                                ) : (
                                  <>
                                    <i
                                      className="fas fa-pen me-2 d-print-none"
                                      onClick={() => {
                                        handleEditRow(
                                          index,
                                          "CurrentMedicationDetails"
                                        );
                                      }}
                                    ></i>
                                    {item.FamilyMember}
                                  </>
                                )}
                              </div>
                              <div className="col border-end py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <Input
                                    name="Medication"
                                    value={item.Medication}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "CurrentMedicationDetails"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  item.Medication
                                )}
                              </div>
                              <div className="col border-end py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <Input
                                    name="Dosage"
                                    value={item.Dosage}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "CurrentMedicationDetails"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  item.Dosage
                                )}
                              </div>
                              <div className="col py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <Input
                                    name="Purpose"
                                    value={item.Purpose}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "CurrentMedicationDetails"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  item.Purpose
                                )}
                              </div>
                            </div>
                          ))
                        : null}
                    </div>
                    {!formDisabled ? (
                      <>
                        <button
                          className="btn btn-primary mb-2"
                          onClick={() => {
                            handleAddRow("CurrentMedicationDetails", {
                              IsEditable: true,
                            });
                          }}
                        >
                          Add Medication
                        </button>
                      </>
                    ) : null}
                    {errors.CurrentMedicationDetails !== undefined && (
                      <div className="row">
                        <div className="col">
                          <div className="alert alert-danger">
                            {errors.CurrentMedicationDetails}
                          </div>
                        </div>
                      </div>
                    )}
                  </FieldTable>
                ) : null}
                <RadioButton
                  name="HadMedications"
                  label="Have any family members previously been prescribed medications?"
                  error={errors.HadMedications}
                  value={data.HadMedications["@value"]}
                  options={yesNoOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.HadMedications["@value"] !== "No" ? (
                  <Input
                    name="PsychotropicMedications"
                    label="Past Psychotropic Medications:"
                    error={errors.PsychotropicMedications}
                    value={data.PsychotropicMedications["@value"]}
                    onChange={handleChange}
                    identical={compare("PsychotropicMedications")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    tooltip="(who, what and reason)"
                  />
                ) : null}
                <FieldTable
                  name="SexualHistoryDetails"
                  commentsHandler={handleComments}
                >
                  <div className="container border-top border-start mb-2">
                    <div className="row border-bottom">
                      <div className="col border-end py-2">Family Member</div>
                      <div className="col border-end py-2">
                        Sexual Orientation
                      </div>
                      <div className="col border-end py-2">Sexual History</div>
                      <div className="col border-end py-2">Description</div>
                    </div>
                    {data.SexualHistoryDetails["@value"] &&
                      JSON.parse(data.SexualHistoryDetails["@value"]).map(
                        (item, index) => (
                          <div key={item._id} className="row border-bottom">
                            <div className="col border-end py-2 d-flex">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <>
                                  <i
                                    className="fas fa-save me-2 d-print-none"
                                    onClick={() => {
                                      handleSaveRow(
                                        index,
                                        "SexualHistoryDetails"
                                      );
                                    }}
                                  ></i>
                                  <i
                                    className="far fa-trash-alt me-2 d-print-none"
                                    onClick={() => {
                                      handleDeleteRow(
                                        index,
                                        "SexualHistoryDetails"
                                      );
                                    }}
                                  ></i>
                                  <Select
                                    name="FamilyMember"
                                    value={item.FamilyMember}
                                    options={familyMemberOptions}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "SexualHistoryDetails"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                  {item.FamilyMember === "Other" ? (
                                    <Input
                                      name="OtherFamilyMember"
                                      value={item.OtherFamilyMember}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "SexualHistoryDetails"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  ) : null}
                                </>
                              ) : (
                                <>
                                  <i
                                    className="fas fa-pen me-2 d-print-none"
                                    onClick={() => {
                                      handleEditRow(
                                        index,
                                        "SexualHistoryDetails"
                                      );
                                    }}
                                  ></i>
                                  {item.OtherFamilyMember || item.FamilyMember}
                                </>
                              )}
                            </div>
                            <div className="col border-end py-2">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <Select
                                  name="SexualOrientation"
                                  value={item.SexualOrientation}
                                  options={sexualOrientationOptions}
                                  onChange={(e) => {
                                    handleRowChange(
                                      e,
                                      index,
                                      "SexualHistoryDetails"
                                    );
                                  }}
                                  disabled={formDisabled}
                                />
                              ) : (
                                item.SexualOrientation
                              )}
                            </div>
                            <div className="col border-end py-2">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <>
                                  {item.SexualHistories !== undefined &&
                                    item.SexualHistories.map(
                                      (type, typesIndex) => (
                                        <div
                                          key={`SexualHistories.${typesIndex}`}
                                          className="d-flex"
                                        >
                                          <i
                                            className="far fa-trash-alt me-2 d-print-none"
                                            onClick={() => {
                                              item.SexualHistories =
                                                item.SexualHistories.slice(
                                                  0,
                                                  typesIndex
                                                ).concat(
                                                  item.SexualHistories.slice(
                                                    typesIndex + 1
                                                  )
                                                );
                                              handleRowChange(
                                                {
                                                  currentTarget: {
                                                    name: "SexualHistories",
                                                    value: item.SexualHistories,
                                                    type: "array",
                                                  },
                                                },
                                                index,
                                                "SexualHistoryDetails"
                                              );
                                            }}
                                          ></i>
                                          {type}
                                        </div>
                                      )
                                    )}
                                  <Select
                                    name="SexualHistories"
                                    options={
                                      item.SexualHistories !== undefined
                                        ? sexualHistoryOptions.filter(
                                            (option) =>
                                              item.SexualHistories.filter(
                                                (sexualHistory) =>
                                                  sexualHistory === option._id
                                              ).length === 0
                                          )
                                        : sexualHistoryOptions
                                    }
                                    onChange={(e) => {
                                      item.SexualHistories !== undefined
                                        ? item.SexualHistories.push(
                                            e.currentTarget.value
                                          )
                                        : (item.SexualHistories = [
                                            e.currentTarget.value,
                                          ]);

                                      handleRowChange(
                                        {
                                          currentTarget: {
                                            name: "SexualHistories",
                                            value: item.SexualHistories,
                                            type: "array",
                                          },
                                        },
                                        index,
                                        "SexualHistoryDetails"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                </>
                              ) : item.SexualHistories !== undefined ? (
                                item.SexualHistories.join(", ")
                              ) : (
                                ""
                              )}
                            </div>
                            <div className="col border-end py-2">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <Input
                                  name="Description"
                                  value={item.Description}
                                  onChange={(e) => {
                                    handleRowChange(
                                      e,
                                      index,
                                      "SexualHistoryDetails"
                                    );
                                  }}
                                  disabled={formDisabled}
                                />
                              ) : (
                                item.Description
                              )}
                            </div>
                          </div>
                        )
                      )}
                  </div>
                  {!formDisabled ? (
                    <button
                      className="btn btn-primary mb-2"
                      onClick={() => {
                        handleAddRow("SexualHistoryDetails", {
                          IsEditable: true,
                        });
                      }}
                    >
                      Add Family Member
                    </button>
                  ) : null}
                  {errors.SexualHistoryDetails && (
                    <div className="alert alert-danger">
                      {errors.SexualHistoryDetails}
                    </div>
                  )}
                </FieldTable>
              </ExpandDiv>
              <ExpandDiv
                id="MentalHealthTreatmentHistory"
                expandStatus={expandStatus}
                className="mt-2"
                title="Mental Health Treatment History"
              >
                <RadioButton
                  name="HasMentalHealthTreatment"
                  label="Have any family members previously been prescribed mental health treatment?"
                  error={errors.HasMentalHealthTreatment}
                  value={data.HasMentalHealthTreatment["@value"]}
                  options={yesNoOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.HasMentalHealthTreatment["@value"] !== "No" && (
                  <FieldTable
                    name="MentalHealthTreatment"
                    commentsHandler={handleComments}
                  >
                    <div className="container border-top border-start border-end mb-2">
                      <div className="row border-bottom">
                        <div className="col border-end py-2">Family Member</div>
                        <div className="col border-end py-2">
                          Reason for Treatment
                        </div>
                        <div className="col border-end py-2">
                          Dates/Provider
                        </div>
                        <div className="col border-end py-2">
                          Satisfaction with Treatment
                        </div>
                        <div className="col py-2">Outcomes</div>
                      </div>
                      {data.MentalHealthTreatment["@value"]
                        ? JSON.parse(data.MentalHealthTreatment["@value"]).map(
                            (item, index) => (
                              <div key={item._id} className="row border-bottom">
                                <div className="col border-end py-2 d-flex">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <>
                                      <i
                                        className="fas fa-save me-2 d-print-none"
                                        onClick={() => {
                                          handleSaveRow(
                                            index,
                                            "MentalHealthTreatment"
                                          );
                                        }}
                                      ></i>
                                      <i
                                        className="far fa-trash-alt me-2 d-print-none"
                                        onClick={() => {
                                          handleDeleteRow(
                                            index,
                                            "MentalHealthTreatment"
                                          );
                                        }}
                                      ></i>
                                      <Input
                                        name="FamilyMember"
                                        value={item.FamilyMember}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "MentalHealthTreatment"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    </>
                                  ) : (
                                    <>
                                      <i
                                        className="fas fa-pen me-2 d-print-none"
                                        onClick={() => {
                                          handleEditRow(
                                            index,
                                            "MentalHealthTreatment"
                                          );
                                        }}
                                      ></i>
                                      {item.FamilyMember}
                                    </>
                                  )}
                                </div>
                                <div className="col border-end py-2">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <Input
                                      name="Reason"
                                      value={item.Reason}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "MentalHealthTreatment"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  ) : (
                                    item.Reason
                                  )}
                                </div>
                                <div className="col border-end py-2">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <Input
                                      name="Dates"
                                      value={item.Dates}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "MentalHealthTreatment"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  ) : (
                                    item.Dates
                                  )}
                                </div>
                                <div className="col border-end py-2">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <Input
                                      name="Satisfaction"
                                      value={item.Satisfaction}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "MentalHealthTreatment"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  ) : (
                                    item.Satisfaction
                                  )}
                                </div>
                                <div className="col py-2">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <Input
                                      name="Outcomes"
                                      value={item.Outcomes}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "MentalHealthTreatment"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  ) : (
                                    item.Outcomes
                                  )}
                                </div>
                              </div>
                            )
                          )
                        : null}
                    </div>
                    {!formDisabled ? (
                      <>
                        <button
                          className="btn btn-primary mb-2"
                          onClick={() => {
                            handleAddRow("MentalHealthTreatment", {
                              IsEditable: true,
                            });
                          }}
                        >
                          Add Treatment
                        </button>
                      </>
                    ) : null}
                    {errors.MentalHealthTreatment !== undefined && (
                      <div className="row">
                        <div className="col">
                          <div className="alert alert-danger">
                            {errors.MentalHealthTreatment}
                          </div>
                        </div>
                      </div>
                    )}
                  </FieldTable>
                )}
              </ExpandDiv>
              <ExpandDiv
                id="Lethality"
                expandStatus={expandStatus}
                className="mt-2"
                title="Lethality"
              >
                <div title="Includes current and past self-injurious behavior, suicidal gestures/thoughts/behaviors, and previous attempts.">
                  <h5>
                    Suicide/Harm to Self: <i className="fas fa-info-circle"></i>
                  </h5>
                </div>
                <Checkbox
                  name="NoSelfHarm"
                  label="No history or current risk of suicide/self-harm"
                  divClassName="ms-3"
                  checked={data.NoSelfHarm["@value"] === "true"}
                  commentsHandler={handleComments}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                {data.NoSelfHarm["@value"] !== "true" && (
                  <>
                    <FieldTable
                      name="SelfHarmDetails"
                      commentsHandler={handleComments}
                    >
                      <div className="container border-top border-start border-end mb-2">
                        <div className="row border-bottom">
                          <div className="col border-end py-2">
                            Family Member
                          </div>
                          <div className="col border-end py-2">
                            History or Current Risk
                          </div>
                          <div className="col py-2">Comments</div>
                        </div>
                        {data.SelfHarmDetails["@value"]
                          ? JSON.parse(data.SelfHarmDetails["@value"]).map(
                              (item, index) => (
                                <div
                                  key={item._id}
                                  className="row border-bottom"
                                >
                                  <div className="col border-end py-2 d-flex">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <>
                                        <i
                                          className="fas fa-save me-2 d-print-none"
                                          onClick={() => {
                                            handleSaveRow(
                                              index,
                                              "SelfHarmDetails"
                                            );
                                          }}
                                        ></i>
                                        <i
                                          className="far fa-trash-alt me-2 d-print-none"
                                          onClick={() => {
                                            handleDeleteRow(
                                              index,
                                              "SelfHarmDetails"
                                            );
                                          }}
                                        ></i>
                                        <Input
                                          name="FamilyMember"
                                          value={item.FamilyMember}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "SelfHarmDetails"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </>
                                    ) : (
                                      <>
                                        <i
                                          className="fas fa-pen me-2 d-print-none"
                                          onClick={() => {
                                            handleEditRow(
                                              index,
                                              "SelfHarmDetails"
                                            );
                                          }}
                                        ></i>
                                        {item.FamilyMember}
                                      </>
                                    )}
                                  </div>
                                  <div className="col border-end py-2">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <>
                                        <Checkbox
                                          name={`SelfAbusive-${index}`}
                                          label="Self-injurious or self-abusive"
                                          divClassName="ms-3"
                                          checked={item.SelfAbusive === "true"}
                                          onChange={(e) => {
                                            e.currentTarget = {
                                              ...e.currentTarget,
                                              type: "checkbox",
                                              name: e.currentTarget.name.split(
                                                "-"
                                              )[0],
                                            };
                                            handleRowChange(
                                              e,
                                              index,
                                              "SelfHarmDetails"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                        <Checkbox
                                          name={`SuicidalThreats-${index}`}
                                          label="Suicidal threats/gestures"
                                          divClassName="ms-3"
                                          checked={
                                            item.SuicidalThreats === "true"
                                          }
                                          onChange={(e) => {
                                            e.currentTarget = {
                                              ...e.currentTarget,
                                              type: "checkbox",
                                              name: e.currentTarget.name.split(
                                                "-"
                                              )[0],
                                            };
                                            handleRowChange(
                                              e,
                                              index,
                                              "SelfHarmDetails"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                        <Checkbox
                                          name={`SuicideAttempt-${index}`}
                                          label="Suicide attempt"
                                          divClassName="ms-3"
                                          checked={
                                            item.SuicideAttempt === "true"
                                          }
                                          onChange={(e) => {
                                            e.currentTarget = {
                                              ...e.currentTarget,
                                              type: "checkbox",
                                              name: e.currentTarget.name.split(
                                                "-"
                                              )[0],
                                            };
                                            handleRowChange(
                                              e,
                                              index,
                                              "SelfHarmDetails"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </>
                                    ) : (
                                      getSelfHarmRisk(index)
                                    )}
                                  </div>
                                  <div className="col py-2">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <Input
                                        name="Comments"
                                        value={item.Comments}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "SelfHarmDetails"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : (
                                      item.Comments
                                    )}
                                  </div>
                                </div>
                              )
                            )
                          : null}
                      </div>
                      {!formDisabled ? (
                        <>
                          <button
                            className="btn btn-primary mb-2"
                            onClick={() => {
                              handleAddRow("SelfHarmDetails", {
                                IsEditable: true,
                              });
                            }}
                          >
                            Add Family Member
                          </button>
                        </>
                      ) : null}
                      {errors.SelfHarmDetails !== undefined && (
                        <div className="row">
                          <div className="col">
                            <div className="alert alert-danger">
                              {errors.SelfHarmDetails}
                            </div>
                          </div>
                        </div>
                      )}
                    </FieldTable>
                  </>
                )}
                <RadioButton
                  name="AllSelfHarmScreened"
                  label="All family members screened for suicidality during this assessment:"
                  error={errors.AllSelfHarmScreened}
                  value={data.AllSelfHarmScreened["@value"]}
                  options={yesNoOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.AllSelfHarmScreened["@value"] === "No" && (
                  <Input
                    name="NotAllSelfHarmScreenedDetails"
                    label="Please explain:"
                    error={errors.NotAllSelfHarmScreenedDetails}
                    value={data.NotAllSelfHarmScreenedDetails["@value"]}
                    onChange={handleChange}
                    identical={compare("NotAllSelfHarmScreenedDetails")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    title="Reason for treatment, dates/provider, satisfaction, outcomes, etc."
                  />
                )}
                {data.NoSelfHarm["@value"] !== "true" && (
                  <>
                    <TextArea
                      name="SelfHarmComments"
                      label="Comments/Action Taken:"
                      error={errors.SelfHarmComments}
                      value={data.SelfHarmComments["@value"]}
                      onChange={handleChange}
                      identical={compare("SelfHarmComments")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.SelfHarmDetails["@value"] &&
                      JSON.parse(data.SelfHarmDetails["@value"]).length > 0 && (
                        <i>
                          Based on how you completed the table, please complete
                          a{" "}
                          <Link
                            to={`/client/${clientid}?tab=forms`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Safety Plan
                          </Link>{" "}
                          and{" "}
                          <Link
                            to={`/client/${clientid}?tab=cpass`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            C-SSRS Assessment
                          </Link>
                          .
                        </i>
                      )}
                    <br />
                  </>
                )}
                <div title="Includes current and past threats/thoughts/acts to intentionally harm/endanger others or animals.">
                  <h5>
                    Intentional Harm to Others/Endangerment:{" "}
                    <i className="fas fa-info-circle"></i>
                  </h5>
                </div>
                <Checkbox
                  name="NoHarmingOthers"
                  label="No history or current risk of harm/endangerment to others"
                  divClassName="ms-3"
                  checked={data.NoHarmingOthers["@value"] === "true"}
                  commentsHandler={handleComments}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                {data.NoHarmingOthers["@value"] !== "true" && (
                  <>
                    <FieldTable
                      name="HarmingOthersDetails"
                      commentsHandler={handleComments}
                    >
                      <div className="container border-top border-start border-end mb-2">
                        <div className="row border-bottom">
                          <div className="col border-end py-2">
                            Family Member
                          </div>
                          <div className="col border-end py-2">
                            History or Current Risk
                          </div>
                          <div className="col py-2">Comments</div>
                        </div>
                        {data.HarmingOthersDetails["@value"]
                          ? JSON.parse(data.HarmingOthersDetails["@value"]).map(
                              (item, index) => (
                                <div
                                  key={item._id}
                                  className="row border-bottom"
                                >
                                  <div className="col border-end py-2 d-flex">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <>
                                        <i
                                          className="fas fa-save me-2 d-print-none"
                                          onClick={() => {
                                            handleSaveRow(
                                              index,
                                              "HarmingOthersDetails"
                                            );
                                          }}
                                        ></i>
                                        <i
                                          className="far fa-trash-alt me-2 d-print-none"
                                          onClick={() => {
                                            handleDeleteRow(
                                              index,
                                              "HarmingOthersDetails"
                                            );
                                          }}
                                        ></i>
                                        <Input
                                          name="FamilyMember"
                                          value={item.FamilyMember}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "HarmingOthersDetails"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </>
                                    ) : (
                                      <>
                                        <i
                                          className="fas fa-pen me-2 d-print-none"
                                          onClick={() => {
                                            handleEditRow(
                                              index,
                                              "HarmingOthersDetails"
                                            );
                                          }}
                                        ></i>
                                        {item.FamilyMember}
                                      </>
                                    )}
                                  </div>
                                  <div className="col border-end py-2">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <>
                                        <Checkbox
                                          name={`Thoughts-${index}`}
                                          label="Thoughts of intentionally harming/endangering others"
                                          divClassName="ms-3"
                                          checked={item.Thoughts === "true"}
                                          onChange={(e) => {
                                            e.currentTarget = {
                                              ...e.currentTarget,
                                              type: "checkbox",
                                              name: e.currentTarget.name.split(
                                                "-"
                                              )[0],
                                            };
                                            handleRowChange(
                                              e,
                                              index,
                                              "SelfHarmDetails"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                        <Checkbox
                                          name={`Threats-${index}`}
                                          label="Threats to intentionally harm/endanger others"
                                          divClassName="ms-3"
                                          checked={item.Threats === "true"}
                                          onChange={(e) => {
                                            e.currentTarget = {
                                              ...e.currentTarget,
                                              type: "checkbox",
                                              name: e.currentTarget.name.split(
                                                "-"
                                              )[0],
                                            };
                                            handleRowChange(
                                              e,
                                              index,
                                              "SelfHarmDetails"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                        <Checkbox
                                          name={`HarmOthers-${index}`}
                                          label="Acts of intentionally harming/endangering others"
                                          divClassName="ms-3"
                                          checked={item.HarmOthers === "true"}
                                          onChange={(e) => {
                                            e.currentTarget = {
                                              ...e.currentTarget,
                                              type: "checkbox",
                                              name: e.currentTarget.name.split(
                                                "-"
                                              )[0],
                                            };
                                            handleRowChange(
                                              e,
                                              index,
                                              "SelfHarmDetails"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                        <Checkbox
                                          name={`HarmAnimals-${index}`}
                                          label="Acts of intentionally harming/endangering animals"
                                          divClassName="ms-3"
                                          checked={item.HarmAnimals === "true"}
                                          onChange={(e) => {
                                            e.currentTarget = {
                                              ...e.currentTarget,
                                              type: "checkbox",
                                              name: e.currentTarget.name.split(
                                                "-"
                                              )[0],
                                            };
                                            handleRowChange(
                                              e,
                                              index,
                                              "SelfHarmDetails"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </>
                                    ) : (
                                      getHarmOthersRisk(index)
                                    )}
                                  </div>
                                  <div className="col py-2">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <Input
                                        name="Comments"
                                        value={item.Comments}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "HarmingOthersDetails"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : (
                                      item.Comments
                                    )}
                                  </div>
                                </div>
                              )
                            )
                          : null}
                      </div>
                      {!formDisabled ? (
                        <>
                          <button
                            className="btn btn-primary mb-2"
                            onClick={() => {
                              handleAddRow("HarmingOthersDetails", {
                                IsEditable: true,
                              });
                            }}
                          >
                            Add Risk
                          </button>
                        </>
                      ) : null}
                      {errors.HarmingOthersDetails !== undefined && (
                        <div className="row">
                          <div className="col">
                            <div className="alert alert-danger">
                              {errors.HarmingOthersDetails}
                            </div>
                          </div>
                        </div>
                      )}
                    </FieldTable>
                  </>
                )}
                <RadioButton
                  name="AllHarmOthersScreened"
                  label="All family members screened for risk of harm/endangerment to others during this assessment:"
                  error={errors.AllHarmOthersScreened}
                  value={data.AllHarmOthersScreened["@value"]}
                  options={yesNoOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.AllHarmOthersScreened["@value"] === "No" && (
                  <Input
                    name="NotAllHarmOthersScreenedDetails"
                    label="Please explain"
                    error={errors.NotAllHarmOthersScreenedDetails}
                    value={data.NotAllHarmOthersScreenedDetails["@value"]}
                    onChange={handleChange}
                    identical={compare("NotAllHarmOthersScreenedDetails")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    title="Reason for treatment, dates/provider, satisfaction, outcomes, etc."
                  />
                )}
                {data.NoHarmingOthers["@value"] !== "true" && (
                  <>
                    <TextArea
                      name="HarmingOthersComments"
                      label="Comments/Action Taken:"
                      error={errors.HarmingOthersComments}
                      value={data.HarmingOthersComments["@value"]}
                      onChange={handleChange}
                      identical={compare("HarmingOthersComments")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.HarmingOthersDetails["@value"] &&
                      JSON.parse(data.HarmingOthersDetails["@value"]).length >
                        0 && (
                        <i>
                          Based on how you completed the table, please complete
                          a{" "}
                          <Link
                            to={`/client/${clientid}?tab=forms`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Safety Plan
                          </Link>{" "}
                          and{" "}
                          <Link
                            to={`/client/${clientid}?tab=cpass`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            C-SSRS Assessment
                          </Link>
                          .
                        </i>
                      )}
                    <br />
                  </>
                )}
              </ExpandDiv>
              <ExpandDiv
                id="Education"
                expandStatus={expandStatus}
                className="mt-2"
                title="Education/Employment"
              >
                <FieldTable
                  name="EducationDetails"
                  commentsHandler={handleComments}
                >
                  <div className="container border-top border-start border-end mb-2">
                    <div className="row border-bottom">
                      <div className="col border-end py-2">Family Member</div>
                      <div className="col border-end py-2">
                        Current Grade or Highest Grade Completed
                      </div>
                      <div className="col py-2">
                        Achievements, Concerns, School Supports, IEP/504
                        Information, etc.
                      </div>
                    </div>
                    {data.EducationDetails["@value"]
                      ? JSON.parse(data.EducationDetails["@value"]).map(
                          (item, index) => (
                            <div key={item._id} className="row border-bottom">
                              <div className="col border-end py-2 d-flex">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <>
                                    <i
                                      className="fas fa-save me-2 d-print-none"
                                      onClick={() => {
                                        handleSaveRow(
                                          index,
                                          "EducationDetails"
                                        );
                                      }}
                                    ></i>
                                    <i
                                      className="far fa-trash-alt me-2 d-print-none"
                                      onClick={() => {
                                        handleDeleteRow(
                                          index,
                                          "EducationDetails"
                                        );
                                      }}
                                    ></i>
                                    <Input
                                      name="FamilyMember"
                                      value={item.FamilyMember}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "EducationDetails"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  </>
                                ) : (
                                  <>
                                    <i
                                      className="fas fa-pen me-2 d-print-none"
                                      onClick={() => {
                                        handleEditRow(
                                          index,
                                          "EducationDetails"
                                        );
                                      }}
                                    ></i>
                                    {item.FamilyMember}
                                  </>
                                )}
                              </div>
                              <div className="col border-end py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <Input
                                    name="Grade"
                                    value={item.Grade}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "EducationDetails"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  item.Grade
                                )}
                              </div>
                              <div className="col py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <Input
                                    name="Comments"
                                    value={item.Comments}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "EducationDetails"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  item.Comments
                                )}
                              </div>
                            </div>
                          )
                        )
                      : null}
                  </div>
                  {!formDisabled ? (
                    <>
                      <button
                        className="btn btn-primary mb-2"
                        onClick={() => {
                          handleAddRow("EducationDetails", {
                            IsEditable: true,
                          });
                        }}
                      >
                        Add Education Information
                      </button>
                    </>
                  ) : null}
                  {errors.EducationDetails !== undefined && (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.EducationDetails}
                        </div>
                      </div>
                    </div>
                  )}
                </FieldTable>
                <h5>Employment</h5>
                <Checkbox
                  name="NoneEmployed"
                  label="No family members currently employed"
                  divClassName="ms-3"
                  checked={data.NoneEmployed["@value"] === "true"}
                  commentsHandler={handleComments}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                {data.NoneEmployed["@value"] !== "true" && (
                  <FieldTable
                    name="EmploymentDetails"
                    commentsHandler={handleComments}
                  >
                    <div className="container border-top border-start border-end mb-2">
                      <div className="row border-bottom">
                        <div className="col border-end py-2">Family Member</div>
                        <div className="col border-end py-2">
                          Place of Employment
                        </div>
                        <div className="col py-2">
                          Issues / Achievements / Barriers to Employment
                          Stability
                        </div>
                      </div>
                      {data.EmploymentDetails["@value"]
                        ? JSON.parse(data.EmploymentDetails["@value"]).map(
                            (item, index) => (
                              <div key={item._id} className="row border-bottom">
                                <div className="col border-end py-2 d-flex">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <>
                                      <i
                                        className="fas fa-save me-2 d-print-none"
                                        onClick={() => {
                                          handleSaveRow(
                                            index,
                                            "EmploymentDetails"
                                          );
                                        }}
                                      ></i>
                                      <i
                                        className="far fa-trash-alt me-2 d-print-none"
                                        onClick={() => {
                                          handleDeleteRow(
                                            index,
                                            "EmploymentDetails"
                                          );
                                        }}
                                      ></i>
                                      <Input
                                        name="FamilyMember"
                                        value={item.FamilyMember}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "EmploymentDetails"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    </>
                                  ) : (
                                    <>
                                      <i
                                        className="fas fa-pen me-2 d-print-none"
                                        onClick={() => {
                                          handleEditRow(
                                            index,
                                            "EmploymentDetails"
                                          );
                                        }}
                                      ></i>
                                      {item.FamilyMember}
                                    </>
                                  )}
                                </div>
                                <div className="col border-end py-2">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <Input
                                      name="Workplace"
                                      value={item.Workplace}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "EmploymentDetails"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  ) : (
                                    item.Workplace
                                  )}
                                </div>
                                <div className="col py-2">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <Input
                                      name="Comments"
                                      value={item.Comments}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "EmploymentDetails"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  ) : (
                                    item.Comments
                                  )}
                                </div>
                              </div>
                            )
                          )
                        : null}
                    </div>
                    {!formDisabled ? (
                      <>
                        <button
                          className="btn btn-primary mb-2"
                          onClick={() => {
                            handleAddRow("EmploymentDetails", {
                              IsEditable: true,
                            });
                          }}
                        >
                          Add Employment Information
                        </button>
                      </>
                    ) : null}
                    {errors.EmploymentDetails !== undefined && (
                      <div className="row">
                        <div className="col">
                          <div className="alert alert-danger">
                            {errors.EmploymentDetails}
                          </div>
                        </div>
                      </div>
                    )}
                  </FieldTable>
                )}
                <RadioButton
                  name="MilitaryHistory"
                  label="Family members with Military History"
                  error={errors.MilitaryHistory}
                  value={data.MilitaryHistory["@value"]}
                  options={yesNoOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.MilitaryHistory["@value"] === "Yes" && (
                  <Input
                    name="MilitaryHistoryDetails"
                    label="Who, dates of service, branch and discharge reason"
                    error={errors.MilitaryHistoryDetails}
                    value={data.MilitaryHistoryDetails["@value"]}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                )}
              </ExpandDiv>
              <ExpandDiv
                id="SubstanceUseHistory"
                expandStatus={expandStatus}
                className="mt-2"
                title="Alcohol/Drug Use History"
              >
                <Checkbox
                  name="NoSubstanceUse"
                  label="No family members with current or past substance use"
                  divClassName="ms-3"
                  error={errors.NoSubstanceUse}
                  checked={data.NoSubstanceUse["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.NoSubstanceUse["@value"] !== "true" ? (
                  <FieldTable
                    name="SubstanceUseHistory"
                    commentsHandler={handleComments}
                  >
                    <div className="container border mb-2">
                      <div className="row border-bottom">
                        <div className="col border-end py-2">Family Member</div>
                        <div className="col border-end py-2">
                          Substances Used
                        </div>
                        <div className="col border-end py-2">Status of Use</div>
                        <div className="col border-end py-2">
                          Amount and Frequency
                        </div>
                        <div className="col border-end py-2">
                          Perception of Use
                        </div>
                        <div className="col py-2">
                          Impact on Functioning / Relationships
                        </div>
                      </div>
                      {data.SubstanceUseHistory["@value"] &&
                        JSON.parse(data.SubstanceUseHistory["@value"]).map(
                          (item, index) => (
                            <div key={item._id} className="row border-bottom">
                              <div className="col border-end py-2 d-flex">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <>
                                    <i
                                      className="fas fa-save me-2 d-print-none"
                                      onClick={() => {
                                        handleSaveRow(
                                          index,
                                          "SubstanceUseHistory"
                                        );
                                      }}
                                    ></i>
                                    <i
                                      className="far fa-trash-alt me-2 d-print-none"
                                      onClick={() => {
                                        handleDeleteRow(
                                          index,
                                          "SubstanceUseHistory"
                                        );
                                      }}
                                    ></i>
                                    <Input
                                      name="FamilyMember"
                                      value={item.FamilyMember}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "SubstanceUseHistory"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  </>
                                ) : (
                                  <>
                                    <i
                                      className="fas fa-pen me-2 d-print-none"
                                      onClick={() => {
                                        handleEditRow(
                                          index,
                                          "SubstanceUseHistory"
                                        );
                                      }}
                                    ></i>
                                    {item.FamilyMember}
                                  </>
                                )}
                              </div>
                              <div className="col border-end py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <TextArea
                                    name="SubstancesUsed"
                                    value={item.SubstancesUsed}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "SubstanceUseHistory"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  item.SubstancesUsed
                                )}
                              </div>
                              <div className="col border-end py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <TextArea
                                    name="Status"
                                    value={item.Status}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "SubstanceUseHistory"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  item.Status
                                )}
                              </div>
                              <div className="col border-end py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <TextArea
                                    name="Amount"
                                    value={item.Amount}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "SubstanceUseHistory"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  item.Amount
                                )}
                              </div>
                              <div className="col border-end py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <TextArea
                                    name="Perception"
                                    value={item.Perception}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "SubstanceUseHistory"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  item.Perception
                                )}
                              </div>
                              <div className="col py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <Input
                                    name="Impact"
                                    value={item.Impact}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "SubstanceUseHistory"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  item.Impact
                                )}
                              </div>
                            </div>
                          )
                        )}
                    </div>
                    {!formDisabled && (
                      <>
                        <button
                          className="btn btn-primary my-2"
                          onClick={() => {
                            handleAddRow("SubstanceUseHistory", {
                              IsEditable: true,
                            });
                          }}
                        >
                          Add Family Member
                        </button>
                      </>
                    )}
                    {errors.SubstanceUseHistory !== undefined ? (
                      <div className="row">
                        <div className="col">
                          <div className="alert alert-danger">
                            {errors.SubstanceUseHistory}
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </FieldTable>
                ) : null}

                <RadioButton
                  name="HasSubstanceUseTreatment"
                  label="Have any family members received alcohol/drug treatment?"
                  error={errors.HasSubstanceUseTreatment}
                  value={data.HasSubstanceUseTreatment["@value"]}
                  options={yesNoOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.HasSubstanceUseTreatment["@value"] !== "No" ? (
                  <FieldTable
                    name="SUTreatmentHistory"
                    commentsHandler={handleComments}
                  >
                    <div className="container border-top border-start border-end mb-2">
                      <div className="row border-bottom">
                        <div className="col border-end py-2">Family Member</div>
                        <div className="col border-end py-2">
                          Agency/Facility
                        </div>
                        <div className="col border-end py-2">
                          Type of Treatment
                        </div>
                        <div className="col border-end py-2">Dates</div>
                        <div className="col border-end py-2">Reason</div>
                        <div className="col py-2">Outcome</div>
                      </div>
                      {data.SUTreatmentHistory["@value"]
                        ? JSON.parse(data.SUTreatmentHistory["@value"]).map(
                            (item, index) => (
                              <div key={item._id} className="row border-bottom">
                                <div className="col border-end py-2 d-flex">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <>
                                      <i
                                        className="fas fa-save me-2 d-print-none"
                                        onClick={() => {
                                          handleSaveRow(
                                            index,
                                            "SUTreatmentHistory"
                                          );
                                        }}
                                      ></i>
                                      <i
                                        className="far fa-trash-alt me-2 d-print-none"
                                        onClick={() => {
                                          handleDeleteRow(
                                            index,
                                            "SUTreatmentHistory"
                                          );
                                        }}
                                      ></i>
                                      <Input
                                        name="FamilyMember"
                                        value={item.FamilyMember}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "SUTreatmentHistory"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    </>
                                  ) : (
                                    <>
                                      <i
                                        className="fas fa-pen me-2 d-print-none"
                                        onClick={() => {
                                          handleEditRow(
                                            index,
                                            "SUTreatmentHistory"
                                          );
                                        }}
                                      ></i>
                                      {item.FamilyMember}
                                    </>
                                  )}
                                </div>
                                <div className="col border-end py-2">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <Input
                                      name="Agency"
                                      value={item.Agency}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "SUTreatmentHistory"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  ) : (
                                    item.Agency
                                  )}
                                </div>
                                <div className="col border-end py-2">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <Select
                                      name="TypeOfTreatment"
                                      value={item.TypeOfTreatment}
                                      options={typeOfTreatmentOptions}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "SUTreatmentHistory"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  ) : (
                                    item.TypeOfTreatment
                                  )}
                                </div>
                                <div className="col border-end py-2">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <Input
                                      name="Dates"
                                      value={item.Dates}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "SUTreatmentHistory"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  ) : (
                                    item.Dates
                                  )}
                                </div>
                                <div className="col border-end py-2">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <Input
                                      name="Reason"
                                      value={item.Reason}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "SUTreatmentHistory"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  ) : (
                                    item.Reason
                                  )}
                                </div>
                                <div className="col py-2">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <Input
                                      name="Outcome"
                                      value={item.Outcome}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "SUTreatmentHistory"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  ) : (
                                    item.Outcome
                                  )}
                                </div>
                              </div>
                            )
                          )
                        : null}
                    </div>
                    {!formDisabled ? (
                      <>
                        <button
                          className="btn btn-primary mb-2"
                          onClick={() => {
                            handleAddRow("SUTreatmentHistory", {
                              IsEditable: true,
                            });
                          }}
                        >
                          Add Treatment History
                        </button>
                      </>
                    ) : null}
                    {errors.SUTreatmentHistory !== undefined ? (
                      <div className="row">
                        <div className="col">
                          <div className="alert alert-danger">
                            {errors.SUTreatmentHistory}
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </FieldTable>
                ) : null}
              </ExpandDiv>
              <ExpandDiv
                id="TraumaHistory"
                expandStatus={expandStatus}
                className="mt-2"
                title="Trauma History"
              >
                <FieldTable
                  name="TraumaHistory"
                  commentsHandler={handleComments}
                >
                  <br />
                  <div className="container border mb-2">
                    <div className="row border-bottom">
                      <div className="col-3 border-end py-2">Family Member</div>
                      <div className="col-3 border-end py-2">
                        Type of Trauma(s)
                      </div>
                      <div className="col-6 py-2">Comments</div>
                    </div>
                    {data.TraumaHistory["@value"]
                      ? JSON.parse(data.TraumaHistory["@value"]).map(
                          (item, index) => (
                            <div key={item._id} className="row border-bottom">
                              <div className="col-3 border-end py-2 d-flex">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <>
                                    <i
                                      className="fas fa-save me-2 d-print-none"
                                      onClick={() => {
                                        handleSaveRow(index, "TraumaHistory");
                                      }}
                                    ></i>
                                    <i
                                      className="far fa-trash-alt me-2 d-print-none"
                                      onClick={() => {
                                        handleDeleteRow(index, "TraumaHistory");
                                      }}
                                    ></i>
                                    <Input
                                      name="FamilyMember"
                                      value={item.FamilyMember}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "TraumaHistory"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  </>
                                ) : (
                                  <>
                                    <i
                                      className="fas fa-pen me-2 d-print-none"
                                      onClick={() => {
                                        handleEditRow(index, "TraumaHistory");
                                      }}
                                    ></i>
                                    {item.FamilyMember}
                                  </>
                                )}
                              </div>
                              <div className="col-3 border-end py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <>
                                    {item.Types !== undefined &&
                                      item.Types.map((type, typesIndex) => (
                                        <div
                                          key={`Types.${typesIndex}`}
                                          className="d-flex"
                                        >
                                          <i
                                            className="far fa-trash-alt me-2 d-print-none"
                                            onClick={() => {
                                              item.Types = item.Types.slice(
                                                0,
                                                typesIndex
                                              ).concat(
                                                item.Types.slice(typesIndex + 1)
                                              );
                                              handleRowChange(
                                                {
                                                  currentTarget: {
                                                    name: "Types",
                                                    value: item.Types,
                                                    type: "array",
                                                  },
                                                },
                                                index,
                                                "TraumaHistory"
                                              );
                                            }}
                                          ></i>
                                          {type}
                                        </div>
                                      ))}
                                    <Select
                                      name="Types"
                                      options={
                                        item.Types !== undefined
                                          ? traumaTypeOptions.filter(
                                              (option) =>
                                                item.Types.filter(
                                                  (type) => type === option._id
                                                ).length === 0
                                            )
                                          : traumaTypeOptions
                                      }
                                      onChange={(e) => {
                                        item.Types !== undefined
                                          ? item.Types.push(
                                              e.currentTarget.value
                                            )
                                          : (item.Types = [
                                              e.currentTarget.value,
                                            ]);

                                        handleRowChange(
                                          {
                                            currentTarget: {
                                              name: "Types",
                                              value: item.Types,
                                              type: "array",
                                            },
                                          },
                                          index,
                                          "TraumaHistory"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  </>
                                ) : item.Types !== undefined ? (
                                  item.Types.join(", ")
                                ) : (
                                  ""
                                )}
                              </div>
                              <div className="col-6 py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <TextArea
                                    name="Comments"
                                    value={item.Comments}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "TraumaHistory"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  item.Comments
                                )}
                              </div>
                            </div>
                          )
                        )
                      : null}
                  </div>
                  {!formDisabled && (
                    <>
                      <button
                        className="btn btn-primary mb-2"
                        onClick={() => {
                          handleAddRow("TraumaHistory", {
                            IsEditable: true,
                          });
                        }}
                      >
                        Add Family Member
                      </button>
                    </>
                  )}
                  {errors.TraumaHistory !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.TraumaHistory}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </FieldTable>
                <RadioButton
                  name="FamilyChanges"
                  label="Changes/transitions in family:"
                  error={errors.FamilyChanges}
                  value={data.FamilyChanges["@value"]}
                  options={yesNoOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.FamilyChanges["@value"] === "Yes" && (
                  <Input
                    name="FamilyChangesDetails"
                    label="Describe:"
                    error={errors.FamilyChangesDetails}
                    value={data.FamilyChangesDetails["@value"]}
                    onChange={handleChange}
                    identical={compare("FamilyChangesDetails")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                )}
                <RadioButton
                  name="RecentGrief"
                  label="Recent or significant deaths/grief issues:"
                  error={errors.RecentGrief}
                  value={data.RecentGrief["@value"]}
                  options={yesNoOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.RecentGrief["@value"] === "Yes" && (
                  <Input
                    name="RecentGriefDetails"
                    label="Describe:"
                    error={errors.RecentGriefDetails}
                    value={data.RecentGriefDetails["@value"]}
                    onChange={handleChange}
                    identical={compare("RecentGriefDetails")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                )}
              </ExpandDiv>
              <ExpandDiv
                id="MentalStatus"
                expandStatus={expandStatus}
                className="mt-2"
                title="Mental Status"
              >
                <Checkbox
                  name="MentalStatusNA"
                  label="N/A (Skip Section)"
                  divClassName="ms-3"
                  checked={data.MentalStatusNA["@value"] === "true"}
                  commentsHandler={handleComments}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                {data.MentalStatusNA["@value"] !== "true" ? (
                  <FieldTable
                    name="MentalStatusDetails"
                    commentsHandler={handleComments}
                  >
                    <div className="container border mb-2">
                      <div className="row border-bottom">
                        <div className="col border-end py-2">Family Member</div>
                        <div className="col border-end py-2">Mental Status</div>
                        <div className="col py-2">Comments</div>
                      </div>
                      {JSON.parse(data.MentalStatusDetails["@value"])?.map(
                        (item, index) => (
                          <div key={item._id} className="row border-bottom">
                            <div className="col border-end py-2 d-flex">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <>
                                  <i
                                    className="fas fa-save me-2 d-print-none"
                                    onClick={() => {
                                      handleSaveRow(
                                        index,
                                        "MentalStatusDetails"
                                      );
                                    }}
                                  ></i>
                                  <i
                                    className="far fa-trash-alt me-2 d-print-none"
                                    onClick={() => {
                                      handleDeleteRow(
                                        index,
                                        "MentalStatusDetails"
                                      );
                                    }}
                                  ></i>
                                  <Input
                                    name="FamilyMember"
                                    value={item.FamilyMember}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "MentalStatusDetails"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                </>
                              ) : (
                                <>
                                  <i
                                    className="fas fa-pen me-2 d-print-none"
                                    onClick={() => {
                                      handleEditRow(
                                        index,
                                        "MentalStatusDetails"
                                      );
                                    }}
                                  ></i>
                                  {item.FamilyMember}
                                </>
                              )}
                            </div>
                            <div className="col border-end py-2">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <>
                                  {mentalStatusCategories.map((category) => (
                                    <div key={category}>
                                      <b>
                                        <i
                                          className="fas fa-pen"
                                          onClick={() => {
                                            handleCategorySelected(
                                              item,
                                              index,
                                              category
                                            );
                                          }}
                                        />{" "}
                                        {category}
                                      </b>
                                      : {item.MentalStatusResults?.[category]}
                                    </div>
                                  ))}
                                </>
                              ) : (
                                mentalStatusCategories.map((category) => (
                                  <div key={category}>
                                    <b>{category}</b>:{" "}
                                    {item.MentalStatusResults?.[category]}
                                  </div>
                                ))
                              )}
                            </div>
                            <div className="col py-2">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <TextArea
                                  name="Comments"
                                  value={item.Comments}
                                  onChange={(e) => {
                                    handleRowChange(
                                      e,
                                      index,
                                      "MentalStatusDetails"
                                    );
                                  }}
                                  disabled={formDisabled}
                                />
                              ) : (
                                item.Comments
                              )}
                            </div>
                          </div>
                        )
                      )}
                    </div>
                    {!formDisabled ? (
                      <button
                        className="btn btn-primary mb-2"
                        onClick={(e) => {
                          handleAddRow("MentalStatusDetails", {
                            IsEditable: true,
                          });
                        }}
                      >
                        Add Family Member
                      </button>
                    ) : null}
                    {errors.MentalStatusDetails !== undefined && (
                      <div className="row">
                        <div className="col">
                          <div className="alert alert-danger">
                            {errors.MentalStatusDetails}
                          </div>
                        </div>
                      </div>
                    )}
                  </FieldTable>
                ) : null}
              </ExpandDiv>
              <ExpandDiv
                id="SymptomChecklist"
                expandStatus={expandStatus}
                className="mt-2"
                title="Symptom Checklist"
              >
                <Checkbox
                  name="SymptomsNA"
                  label="N/A (Skip Section)"
                  divClassName="ms-3"
                  checked={
                    data.SymptomsNA !== undefined &&
                    data.SymptomsNA["@value"] === "true"
                  }
                  comments={parsedComments}
                  addComment={() => setShowComment(true)}
                  editComment={toggleCommentModal}
                  deleteComment={handleDeleteComment}
                  acknowledgeComment={handleAcknowledgeComment}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                {data.SymptomsNA["@value"] !== "true" ? (
                  <FieldTable name="Symptoms" commentsHandler={handleComments}>
                    <div className="container border mb-2">
                      <div className="row border-bottom">
                        <div className="col border-end py-2">Family Member</div>
                        <div className="col border-end py-2">Symptom(s)</div>
                        <div className="col py-2">
                          Description{" "}
                          <i>(including Frequency, Intensity and Duration)</i>
                        </div>
                      </div>
                      {data.Symptoms["@value"] &&
                        JSON.parse(data.Symptoms["@value"]).map(
                          (item, index) => (
                            <div key={item._id} className="row border-bottom">
                              <div className="col border-end py-2 d-flex">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <>
                                    <i
                                      className="fas fa-save me-2 d-print-none"
                                      onClick={() => {
                                        handleSaveRow(index, "Symptoms");
                                      }}
                                    ></i>
                                    <i
                                      className="far fa-trash-alt me-2 d-print-none"
                                      onClick={() => {
                                        handleDeleteRow(index, "Symptoms");
                                      }}
                                    ></i>
                                    <Input
                                      name="FamilyMember"
                                      value={item.FamilyMember}
                                      onChange={(e) => {
                                        handleRowChange(e, index, "Symptoms");
                                      }}
                                      disabled={formDisabled}
                                    />
                                  </>
                                ) : (
                                  <>
                                    <i
                                      className="fas fa-pen me-2 d-print-none"
                                      onClick={() => {
                                        handleEditRow(index, "Symptoms");
                                      }}
                                    ></i>
                                    {item.FamilyMember}
                                  </>
                                )}
                              </div>
                              <div className="col border-end py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <>
                                    {item.Symptoms !== undefined &&
                                      item.Symptoms.map(
                                        (symptom, symptomsIndex) => (
                                          <div
                                            key={`Symptoms.${symptomsIndex}`}
                                            className="d-flex"
                                          >
                                            <i
                                              className="far fa-trash-alt me-2 d-print-none"
                                              onClick={() => {
                                                item.Symptoms =
                                                  item.Symptoms.slice(
                                                    0,
                                                    symptomsIndex
                                                  ).concat(
                                                    item.Symptoms.slice(
                                                      symptomsIndex + 1
                                                    )
                                                  );
                                                handleRowChange(
                                                  {
                                                    currentTarget: {
                                                      name: "Symptoms",
                                                      value: item.Symptoms,
                                                      type: "array",
                                                    },
                                                  },
                                                  index,
                                                  "Symptoms"
                                                );
                                              }}
                                            ></i>
                                            {symptom}
                                          </div>
                                        )
                                      )}
                                    <Select
                                      name="Symptoms"
                                      options={
                                        item.Symptoms !== undefined
                                          ? symptomOptions.filter(
                                              (option) =>
                                                item.Symptoms.filter(
                                                  (symptom) =>
                                                    symptom === option._id
                                                ).length === 0
                                            )
                                          : symptomOptions
                                      }
                                      onChange={(e) => {
                                        handleSymptomSelected(
                                          item,
                                          index,
                                          e.currentTarget.value
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                    {item.Symptoms !== undefined &&
                                      item.Symptoms.filter(
                                        (symptom) => symptom === "Other"
                                      ).length > 0 && (
                                        <Input
                                          name="OtherSymptomDetails"
                                          value={item.OtherSymptomDetails}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "Symptoms"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      )}
                                  </>
                                ) : item.Symptoms !== undefined ? (
                                  `${item.Symptoms.join(", ")}${
                                    item.OtherSymptomDetails !== undefined &&
                                    item.OtherSymptomDetails !== ""
                                      ? `, ${item.OtherSymptomDetails}`
                                      : ""
                                  }`
                                ) : (
                                  ""
                                )}
                              </div>
                              <div className="col py-2">
                                {item.IsEditable === undefined ||
                                item.IsEditable ? (
                                  <TextArea
                                    name="Description"
                                    value={item.Description}
                                    onChange={(e) => {
                                      handleRowChange(e, index, "Symptoms");
                                    }}
                                    disabled={formDisabled}
                                  />
                                ) : (
                                  item.Description
                                )}
                              </div>
                            </div>
                          )
                        )}
                    </div>
                    {!formDisabled ? (
                      <button
                        className="btn btn-primary mb-2"
                        onClick={() => {
                          handleAddRow("Symptoms", {
                            IsEditable: true,
                          });
                        }}
                      >
                        Add Family Member
                      </button>
                    ) : null}
                    {errors.Symptoms !== undefined && (
                      <div className="row">
                        <div className="col">
                          <div className="alert alert-danger">
                            {errors.Symptoms}
                          </div>
                        </div>
                      </div>
                    )}
                  </FieldTable>
                ) : null}
                {data.SymptomsNA["@value"] === "true" && (
                  <>
                    <Input
                      name="SymptomsNAComments"
                      label="Comments:"
                      error={errors.SymptomsNAComments}
                      value={
                        data.SymptomsNAComments &&
                        data.SymptomsNAComments["@value"]
                      }
                      onChange={handleChange}
                      identical={compare("SymptomsNAComments")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </>
                )}
              </ExpandDiv>
              <ExpandDiv
                id="Activities"
                expandStatus={expandStatus}
                className="mt-2"
                title="Activities of Daily Living"
              >
                <div className="container">
                  <div className="row border-bottom mb-2">
                    <div className="col-2">
                      <b>Skill</b>
                    </div>
                    <div className="col-2">
                      <b>Support Needed</b>
                    </div>
                    <div className="col">
                      <b>Family Member(s)</b>
                    </div>
                    <div className="col">
                      <b>Explanation</b>
                    </div>
                  </div>
                  <div className="row border-bottom mt-2">
                    <div className="col-2">
                      Personal appearance and hygiene:
                    </div>
                    <div className="col-2">
                      <RadioButton
                        name="Hygiene"
                        error={errors.Hygiene}
                        value={data.Hygiene["@value"]}
                        options={yesNoOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.Hygiene["@value"] === "Yes" && (
                        <Input
                          name="HygieneFamilyMembers"
                          error={errors.HygieneFamilyMembers}
                          value={data.HygieneFamilyMembers["@value"]}
                          onChange={handleChange}
                          identical={compare("HygieneFamilyMembers")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                    <div className="col">
                      {data.Hygiene["@value"] === "Yes" && (
                        <Input
                          name="HygieneDetails"
                          error={errors.HygieneDetails}
                          value={data.HygieneDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("HygieneDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row border-bottom mt-2">
                    <div className="col-2">Money management:</div>
                    <div className="col-2">
                      <RadioButton
                        name="MoneyManagement"
                        error={errors.MoneyManagement}
                        value={data.MoneyManagement["@value"]}
                        options={yesNoOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.MoneyManagement["@value"] === "Yes" && (
                        <Input
                          name="MoneyManagementFamilyMembers"
                          error={errors.MoneyManagementFamilyMembers}
                          value={data.MoneyManagementFamilyMembers["@value"]}
                          onChange={handleChange}
                          identical={compare("MoneyManagementFamilyMembers")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                    <div className="col">
                      {data.MoneyManagement["@value"] === "Yes" && (
                        <Input
                          name="MoneyManagementDetails"
                          error={errors.MoneyManagementDetails}
                          value={data.MoneyManagementDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("MoneyManagementDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row border-bottom mt-2">
                    <div className="col-2">Food management:</div>
                    <div className="col-2">
                      <RadioButton
                        name="FoodManagement"
                        error={errors.FoodManagement}
                        value={data.FoodManagement["@value"]}
                        options={yesNoOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.FoodManagement["@value"] === "Yes" && (
                        <Input
                          name="FoodManagementFamilyMembers"
                          error={errors.FoodManagementFamilyMembers}
                          value={data.FoodManagementFamilyMembers["@value"]}
                          onChange={handleChange}
                          identical={compare("FoodManagementFamilyMembers")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                    <div className="col">
                      {data.FoodManagement["@value"] === "Yes" && (
                        <Input
                          name="FoodManagementDetails"
                          error={errors.FoodManagementDetails}
                          value={data.FoodManagementDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("FoodManagementDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row border-bottom mt-2">
                    <div className="col-2">
                      Health awareness / Personal Health:
                    </div>
                    <div className="col-2">
                      <RadioButton
                        name="Health"
                        error={errors.Health}
                        value={data.Health["@value"]}
                        options={yesNoOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.Health["@value"] === "Yes" && (
                        <Input
                          name="HealthFamilyMembers"
                          error={errors.HealthFamilyMembers}
                          value={data.HealthFamilyMembers["@value"]}
                          onChange={handleChange}
                          identical={compare("HealthFamilyMembers")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                    <div className="col">
                      {data.Health["@value"] === "Yes" && (
                        <Input
                          name="HealthDetails"
                          error={errors.HealthDetails}
                          value={data.HealthDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("HealthDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row border-bottom mt-2">
                    <div className="col-2">Housekeeping:</div>
                    <div className="col-2">
                      <RadioButton
                        name="Housekeeping"
                        error={errors.Housekeeping}
                        value={data.Housekeeping["@value"]}
                        options={yesNoOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.Housekeeping["@value"] === "Yes" && (
                        <Input
                          name="HousekeepingFamilyMembers"
                          error={errors.HousekeepingFamilyMembers}
                          value={data.HousekeepingFamilyMembers["@value"]}
                          onChange={handleChange}
                          identical={compare("HousekeepingFamilyMembers")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                    <div className="col">
                      {data.Housekeeping["@value"] === "Yes" && (
                        <Input
                          name="HousekeepingDetails"
                          error={errors.HousekeepingDetails}
                          value={data.HousekeepingDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("HousekeepingDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row border-bottom mt-2">
                    <div className="col-2">Transportation:</div>
                    <div className="col-2">
                      <RadioButton
                        name="Transportation"
                        error={errors.Transportation}
                        value={data.Transportation["@value"]}
                        options={yesNoOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.Transportation["@value"] === "Yes" && (
                        <Input
                          name="TransportationFamilyMembers"
                          error={errors.TransportationFamilyMembers}
                          value={data.TransportationFamilyMembers["@value"]}
                          onChange={handleChange}
                          identical={compare("TransportationFamilyMembers")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                    <div className="col">
                      {data.Transportation["@value"] === "Yes" && (
                        <Input
                          name="TransportationDetails"
                          error={errors.TransportationDetails}
                          value={data.TransportationDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("TransportationDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row border-bottom mt-2">
                    <div className="col-2">Emergency and safety skills:</div>
                    <div className="col-2">
                      <RadioButton
                        name="SafetySkills"
                        error={errors.SafetySkills}
                        value={data.SafetySkills["@value"]}
                        options={yesNoOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.SafetySkills["@value"] === "Yes" && (
                        <Input
                          name="SafetySkillsFamilyMembers"
                          error={errors.SafetySkillsFamilyMembers}
                          value={data.SafetySkillsFamilyMembers["@value"]}
                          onChange={handleChange}
                          identical={compare("SafetySkillsFamilyMembers")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                    <div className="col">
                      {data.SafetySkills["@value"] === "Yes" && (
                        <Input
                          name="SafetySkillsDetails"
                          error={errors.SafetySkillsDetails}
                          value={data.SafetySkillsDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("SafetySkillsDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row border-bottom mt-2">
                    <div className="col-2">
                      Knowledge of community resources:
                    </div>
                    <div className="col-2">
                      <RadioButton
                        name="ResourceKnowledge"
                        error={errors.ResourceKnowledge}
                        value={data.ResourceKnowledge["@value"]}
                        options={yesNoOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.ResourceKnowledge["@value"] === "Yes" && (
                        <Input
                          name="ResourceKnowledgeFamilyMembers"
                          error={errors.ResourceKnowledgeFamilyMembers}
                          value={data.ResourceKnowledgeFamilyMembers["@value"]}
                          onChange={handleChange}
                          identical={compare("ResourceKnowledgeFamilyMembers")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                    <div className="col">
                      {data.ResourceKnowledge["@value"] === "Yes" && (
                        <Input
                          name="ResourceKnowledgeDetails"
                          error={errors.ResourceKnowledgeDetails}
                          value={data.ResourceKnowledgeDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("ResourceKnowledgeDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row border-bottom mt-2">
                    <div className="col-2">Time management:</div>
                    <div className="col-2">
                      <RadioButton
                        name="TimeManagement"
                        error={errors.TimeManagement}
                        value={data.TimeManagement["@value"]}
                        options={yesNoOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.TimeManagement["@value"] === "Yes" && (
                        <Input
                          name="TimeManagementFamilyMembers"
                          error={errors.TimeManagementFamilyMembers}
                          value={data.TimeManagementFamilyMembers["@value"]}
                          onChange={handleChange}
                          identical={compare("TimeManagementFamilyMembers")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                    <div className="col">
                      {data.TimeManagement["@value"] === "Yes" && (
                        <Input
                          name="TimeManagementDetails"
                          error={errors.TimeManagementDetails}
                          value={data.TimeManagementDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("TimeManagementDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row border-bottom mt-2">
                    <div className="col-2">Interactions with Others:</div>
                    <div className="col-2">
                      <RadioButton
                        name="InteractionWithOthers"
                        error={errors.InteractionWithOthers}
                        value={data.InteractionWithOthers["@value"]}
                        options={yesNoOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.InteractionWithOthers["@value"] === "Yes" && (
                        <Input
                          name="InteractionFamilyMembers"
                          error={errors.InteractionFamilyMembers}
                          value={data.InteractionFamilyMembers["@value"]}
                          onChange={handleChange}
                          identical={compare("InteractionFamilyMembers")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                    <div className="col">
                      {data.InteractionWithOthers["@value"] === "Yes" && (
                        <Input
                          name="InteractionDetails"
                          error={errors.InteractionDetails}
                          value={data.InteractionDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("InteractionDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                </div>
                <div title="Include information on social relationships/friendships, involvement in the community, bullying concerns, peer associations for children, play skills, socioemotional functioning, communication skills and problem-solving.">
                  Psychosocial Functioning:{" "}
                  <i className="fas fa-info-circle"></i>
                </div>
                <TextArea
                  name="PsychosocialFunctioning"
                  error={errors.PsychosocialFunctioning}
                  value={data.PsychosocialFunctioning["@value"]}
                  onChange={handleChange}
                  identical={compare("PsychosocialFunctioning")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              </ExpandDiv>
              <ExpandDiv
                id="LegalHistory"
                expandStatus={expandStatus}
                className="mt-2"
                title="Legal History"
                tooltip="Type of involvement, description, dates, disposition, outcomes"
              >
                <RadioButton
                  name="LegalInvolvement"
                  error={errors.LegalInvolvement}
                  value={data.LegalInvolvement["@value"]}
                  options={legalHistoryOptions}
                  onChange={handleChange}
                  identical={compare("LegalInvolvement")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.LegalInvolvement["@value"] === "Yes" && (
                  <TextArea
                    name="LegalInvolvementDetails"
                    label="Legal History:"
                    error={errors.LegalInvolvementDetails}
                    value={data.LegalInvolvementDetails["@value"]}
                    onChange={handleChange}
                    identical={compare("LegalInvolvementDetails")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                )}
                <RadioButton
                  name="CPSInvolvement"
                  error={errors.CPSInvolvement}
                  value={data.CPSInvolvement["@value"]}
                  options={cpsInvolvementOptions}
                  onChange={handleChange}
                  identical={compare("CPSInvolvement")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.CPSInvolvement["@value"] === "Yes" && (
                  <TextArea
                    name="CPSInvolvementDetails"
                    label="CPS History:"
                    error={errors.CPSInvolvementDetails}
                    value={data.CPSInvolvementDetails["@value"]}
                    onChange={handleChange}
                    identical={compare("CPSInvolvementDetails")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                )}
              </ExpandDiv>
              <ExpandDiv
                id="AssessmentSummary"
                expandStatus={expandStatus}
                className="mt-2"
                title="Assessment Summary"
              >
                <h5>PFS-2 Scores:</h5>
                <RadioButton
                  name="PFS2CompletionStatus"
                  error={errors.PFS2CompletionStatus}
                  value={data.PFS2CompletionStatus["@value"]}
                  options={completedOptions}
                  onChange={handleChange}
                  identical={compare("PFS2CompletionStatus")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.PFS2CompletionStatus["@value"] === "Completed" && (
                  <>
                    <div className="row">
                      <div className="col">
                        <Input
                          name="Resilience"
                          label="Family Functioning/Resilience:"
                          error={errors.Resilience}
                          value={data.Resilience["@value"]}
                          onChange={handleChange}
                          identical={compare("Resilience")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        <Input
                          name="Attachment"
                          label="Nurturing and Attachment:"
                          error={errors.Attachment}
                          value={data.Attachment["@value"]}
                          onChange={handleChange}
                          identical={compare("Attachment")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col">
                        <Input
                          name="SocialSupports"
                          label="Social Supports:"
                          error={errors.SocialSupports}
                          value={data.SocialSupports["@value"]}
                          onChange={handleChange}
                          identical={compare("SocialSupports")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        <Input
                          name="PractitionerRelationship"
                          label="Caregiver/Practitioner Relationship:"
                          error={errors.PractitionerRelationship}
                          value={data.PractitionerRelationship["@value"]}
                          onChange={handleChange}
                          identical={compare("PractitionerRelationship")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-6">
                        <Input
                          name="ConcreteSupports"
                          label="Concrete Supports:"
                          error={errors.ConcreteSupports}
                          value={data.ConcreteSupports["@value"]}
                          onChange={handleChange}
                          identical={compare("ConcreteSupports")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    </div>
                  </>
                )}
                <h5>Other tools used:</h5>
                <RadioButton
                  name="OtherTools"
                  error={errors.OtherTools}
                  value={data.OtherTools["@value"]}
                  options={yesNoOptions}
                  onChange={handleChange}
                  identical={compare("OtherTools")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.OtherTools["@value"] === "Yes" && (
                  <Input
                    name="OtherToolsDetails"
                    label="Describe:"
                    error={errors.OtherToolsDetails}
                    value={data.OtherToolsDetails["@value"]}
                    onChange={handleChange}
                    identical={compare("OtherToolsDetails")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                )}
                {data.PFS2CompletionStatus["@value"] === "Completed" ||
                data.OtherTools["@value"] === "Yes" ? (
                  <>
                    <h5>Brief Interpretation of Scores:</h5>
                    <TextArea
                      name="Interpretation"
                      error={errors.Interpretation}
                      value={data.Interpretation["@value"]}
                      onChange={handleChange}
                      identical={compare("Interpretation")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </>
                ) : null}
                <h5>Clinical Observations/Family Clinical Summary:</h5>
                <TextArea
                  name="ClinicalSummary"
                  error={errors.ClinicalSummary}
                  value={data.ClinicalSummary["@value"]}
                  onChange={handleChange}
                  identical={compare("ClinicalSummary")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <div title="Concerns include (Safety, supervision, parenting skills, medical, mental health, behavioral, family relations, financial, employment, education, housing/home environment, caregiver issues, hygiene, safe sleep, substance use)">
                  <h5>
                    Areas of Concern/Risk Factors/Potential Barriers:{" "}
                    <i className="fas fa-info-circle"></i>
                  </h5>
                </div>
                <TextArea
                  name="RiskFactors"
                  error={errors.RiskFactors}
                  value={data.RiskFactors["@value"]}
                  onChange={handleChange}
                  identical={compare("RiskFactors")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <h5>Concrete Needs:</h5>
                <TextArea
                  name="ConcreteNeeds"
                  error={errors.ConcreteNeeds}
                  value={data.ConcreteNeeds["@value"]}
                  onChange={handleChange}
                  identical={compare("ConcreteNeeds")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <h5>Family's Perspective/Plan for Discharge:</h5>
                <TextArea
                  name="DischargePlan"
                  error={errors.DischargePlan}
                  value={data.DischargePlan["@value"]}
                  onChange={handleChange}
                  identical={compare("DischargePlan")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              </ExpandDiv>
              <ExpandDiv
                id="Diagnosis"
                expandStatus={expandStatus}
                className="mt-2"
                title="Diagnosis/Evaluation"
              >
                <FieldTable
                  name="DiagnosisDetails"
                  commentsHandler={handleComments}
                >
                  <div className="container border mb-2">
                    <div className="row border-bottom">
                      <div className="col-3 border-end py-2">Family Member</div>
                      <div className="col-9 border-end py-2">
                        ICD-10 Code and Classification
                      </div>
                    </div>
                    {data.DiagnosisDetails["@value"] &&
                      JSON.parse(data.DiagnosisDetails["@value"]).map(
                        (item, index) => (
                          <div key={item._id} className="row border-bottom">
                            <div className="col-3 border-end py-2 d-flex">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <>
                                  <i
                                    className="fas fa-save me-2 d-print-none"
                                    onClick={() => {
                                      handleSaveRow(index, "DiagnosisDetails");
                                    }}
                                  ></i>
                                  <i
                                    className="far fa-trash-alt me-2 d-print-none"
                                    onClick={() => {
                                      handleDeleteRow(
                                        index,
                                        "DiagnosisDetails"
                                      );
                                    }}
                                  ></i>
                                  <Input
                                    name="FamilyMember"
                                    value={item.FamilyMember}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "DiagnosisDetails"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                </>
                              ) : (
                                <>
                                  <i
                                    className="fas fa-pen me-2 d-print-none"
                                    onClick={() => {
                                      handleEditRow(index, "DiagnosisDetails");
                                    }}
                                  ></i>
                                  {item.FamilyMember}
                                </>
                              )}
                            </div>
                            <div className="col-9 py-2">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <>
                                  <Select
                                    name="Code"
                                    value={item.Code}
                                    options={diagnosisOptions}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "DiagnosisDetails"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                  {item.Code === "Other" && (
                                    <Input
                                      name="OtherCode"
                                      value={item.OtherCode}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "DiagnosisDetails"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  )}
                                </>
                              ) : item.OtherCode !== undefined &&
                                item.OtherCode !== "" ? (
                                item.OtherCode
                              ) : (
                                item.Code
                              )}
                            </div>
                          </div>
                        )
                      )}
                  </div>
                  {!formDisabled ? (
                    <button
                      className="btn btn-primary mb-2"
                      onClick={() => {
                        handleAddRow("DiagnosisDetails", {
                          IsEditable: true,
                        });
                      }}
                    >
                      Add Diagnosis
                    </button>
                  ) : null}
                  {errors.DiagnosisDetails !== undefined && (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.DiagnosisDetails}
                        </div>
                      </div>
                    </div>
                  )}
                </FieldTable>
              </ExpandDiv>
              <ExpandDiv
                id="TreatmentRecommendations"
                expandStatus={expandStatus}
                className="mt-2"
                title="Treatment Recommendations/Needs"
              >
                <FieldTable
                  name="TreatmentRecommendations"
                  commentsHandler={handleComments}
                >
                  <b>Treatment Recommendations</b>
                  <div className="container border mb-2">
                    <div className="row border-bottom">
                      <div className="col border-end py-2">Service Type</div>
                      <div className="col border-end py-2">
                        Service Recipient
                      </div>
                      <div className="col border-end py-2">
                        Target Treatment Goal(s)
                      </div>
                      <div className="col py-2">
                        <div title="Concerns include (Safety, supervision, parenting skills, medical, mental health, behavioral, family relations, financial, employment, education, housing/home environment, caregiver issues, hygiene, safe sleep, substance use)">
                          Intervention(s) &amp; Service Delivery{" "}
                          <i>(Frequency/Duration of Service, Modality)</i>
                        </div>
                      </div>
                    </div>
                    {data.TreatmentRecommendations["@value"] &&
                      JSON.parse(data.TreatmentRecommendations["@value"]).map(
                        (item, index) => (
                          <div key={item._id} className="row border-bottom">
                            <div className="col border-end py-2 d-flex">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <>
                                  <i
                                    className="fas fa-save me-2 d-print-none"
                                    onClick={() => {
                                      handleSaveRow(
                                        index,
                                        "TreatmentRecommendations"
                                      );
                                    }}
                                  ></i>
                                  <i
                                    className="far fa-trash-alt me-2 d-print-none"
                                    onClick={() => {
                                      handleDeleteRow(
                                        index,
                                        "TreatmentRecommendations"
                                      );
                                    }}
                                  ></i>
                                  <Select
                                    name="ServiceType"
                                    value={item.ServiceType}
                                    options={serviceTypeOptions}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "TreatmentRecommendations"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                </>
                              ) : (
                                <>
                                  <i
                                    className="fas fa-pen me-2 d-print-none"
                                    onClick={() => {
                                      handleEditRow(
                                        index,
                                        "TreatmentRecommendations"
                                      );
                                    }}
                                  ></i>
                                  {item.ServiceType}
                                </>
                              )}
                            </div>
                            <div className="col border-end py-2">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <Input
                                  name="Recipient"
                                  value={item.Recipient}
                                  onChange={(e) => {
                                    handleRowChange(
                                      e,
                                      index,
                                      "TreatmentRecommendations"
                                    );
                                  }}
                                  disabled={formDisabled}
                                />
                              ) : (
                                item.Recipient
                              )}
                            </div>
                            <div className="col border-end py-2">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <Input
                                  name="Goals"
                                  value={item.Goals}
                                  onChange={(e) => {
                                    handleRowChange(
                                      e,
                                      index,
                                      "TreatmentRecommendations"
                                    );
                                  }}
                                  disabled={formDisabled}
                                />
                              ) : (
                                item.Goals
                              )}
                            </div>
                            <div className="col py-2">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <Input
                                  name="Interventions"
                                  value={item.Interventions}
                                  onChange={(e) => {
                                    handleRowChange(
                                      e,
                                      index,
                                      "TreatmentRecommendations"
                                    );
                                  }}
                                  disabled={formDisabled}
                                />
                              ) : (
                                item.Interventions
                              )}
                            </div>
                          </div>
                        )
                      )}
                  </div>
                  {!formDisabled && (
                    <>
                      <button
                        className="btn btn-primary mb-2"
                        onClick={() => {
                          handleAddRow("TreatmentRecommendations", {
                            IsEditable: true,
                          });
                        }}
                      >
                        Add Treatment Recommendation
                      </button>
                    </>
                  )}
                  {errors.TreatmentRecommendations !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.TreatmentRecommendations}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </FieldTable>
                <h5>Additional Evaluations Needed:</h5>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="AdditionalPsychiatric"
                      label="Psychiatric"
                      checked={data.AdditionalPsychiatric["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="AdditionalPsychological"
                      label="Psychological"
                      checked={
                        data.AdditionalPsychological["@value"] === "true"
                      }
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="AdditionalNeurological"
                      label="Neurological"
                      checked={data.AdditionalNeurological["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="AdditionalEducational"
                      label="Educational"
                      checked={data.AdditionalEducational["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="AdditionalMedical"
                      label="Medical"
                      checked={data.AdditionalMedical["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="AdditionalDental"
                      label="Dental"
                      checked={data.AdditionalDental["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="AdditionalSubstanceAbuse"
                      label="Substance Abuse"
                      checked={
                        data.AdditionalSubstanceAbuse["@value"] === "true"
                      }
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="AdditionalVocational"
                      label="Vocational"
                      checked={data.AdditionalVocational["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="AdditionalOccupational"
                      label="Occupational"
                      checked={data.AdditionalOccupational["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="AdditionalNutritional"
                      label="Nutritional"
                      checked={data.AdditionalNutritional["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="AdditionalVisualAuditory"
                      label="Visual/Auditory"
                      checked={
                        data.AdditionalVisualAuditory["@value"] === "true"
                      }
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="AdditionalDomesticViolence"
                      label="Domestic Violence"
                      checked={
                        data.AdditionalDomesticViolence["@value"] === "true"
                      }
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="AdditionalSexOffender"
                      label="Sexual Offender"
                      checked={data.AdditionalSexOffender["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="AdditionalOther"
                      label={`Other${
                        data.AdditionalOther["@value"] === "true" ? ":" : ""
                      }`}
                      checked={data.AdditionalOther["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.AdditionalOther["@value"] === "true" && (
                      <Input
                        name="AdditionalOtherDetails"
                        error={errors.AdditionalOtherDetails}
                        value={data.AdditionalOtherDetails["@value"]}
                        onChange={handleChange}
                        identical={compare("AdditionalOtherDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                  </div>
                  <div className="col"></div>
                  <div className="col"></div>
                </div>
                {data.AdditionalPsychiatric["@value"] === "true" ||
                data.AdditionalPsychological["@value"] === "true" ||
                data.AdditionalNeurological["@value"] === "true" ||
                data.AdditionalEducational["@value"] === "true" ||
                data.AdditionalMedical["@value"] === "true" ||
                data.AdditionalDental["@value"] === "true" ||
                data.AdditionalSubstanceAbuse["@value"] === "true" ||
                data.AdditionalVocational["@value"] === "true" ||
                data.AdditionalOccupational["@value"] === "true" ||
                data.AdditionalNutritional["@value"] === "true" ||
                data.AdditionalVisualAuditory["@value"] === "true" ||
                data.AdditionalDomesticViolence["@value"] === "true" ||
                data.AdditionalSexOffender["@value"] === "true" ||
                data.AdditionalOther["@value"] === "true" ? (
                  <div className="row">
                    <div className="col">
                      <Input
                        name="MembersInNeed"
                        label="Family Member(s) in need of evaluation:"
                        error={errors.MembersInNeed}
                        value={data.MembersInNeed["@value"]}
                        onChange={handleChange}
                        identical={compare("MembersInNeed")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                ) : null}
              </ExpandDiv>
              {showNarrative === "Show" && (
                <div className="clinicalNarrative bg-white border p-2">
                  <label>
                    <b>Clinical Observations/Family Clinical Summary:</b>
                  </label>
                  <textarea
                    value={data.ClinicalSummary["@value"]}
                    rows="5"
                    disabled
                  />
                </div>
              )}
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).finalSignType
              }
              initialSignatureType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).signatureType
              }
            ></Signature>
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            ></Comment>
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              ></ShareModal>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default INKSPPSD;
